import { Entity, EntityConfig } from './Entity';
import { Vector2, ClassName, ElementalResistances, EquipmentSlot, EquipmentItem } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { InputManager } from '@client/input/InputManager';
import { BASE_STATS, COMBAT, MAX_LEVEL } from '@shared/constants';
import { IsometricMap } from '@client/world/IsometricMap';
import { Inventory } from '@client/inventory/Inventory';
import { GameItem, createEquipmentItem } from '@shared/items/Item';
import { PotionBelt, PotionEffect } from '@shared/items/ConsumableSystem';
import { CustomSkill, SkillCreator, SkillHotbar, HotbarSkill, isSkillV2 } from '@shared/skills/SkillCreator';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { getWeaponTypeFromName, isSkillAllowedForWeapon } from '@shared/skills/SkillTypes';
import { initializeStarterSkills, initializeStarterEffectStones } from '@shared/skills/SkillTemplates';
import { PassiveTreeManager, PassiveBonuses } from '@shared/passives/PassiveTree';
import { getAudioManager } from '@client/audio/AudioManager';
import {
  StatCalculator,
  extractEquipmentModifiers,
  createPassiveSource,
  createPassiveModifierSource,
  createModifier,
  getTriggerEffectManager,
  TriggerEffectManager,
} from '@shared/modifiers';
import { debugLog } from '@shared/utils/Logger';
import { getDamageNumbers } from '@client/systems/DamageNumbers';
import { getSetBonusModifiersBySetIds } from '@shared/items/SetItemSystem';
import { getPlayerShadowCanvas } from '@client/core/GradientCache';
import { LPCSpriteRenderer, LPCSpriteLayer, GameAnimState } from '@client/core/LPCSpriteRenderer';
import { getParticleSystem } from '@client/effects/ParticleSystem';
import { getScreenShake } from '@client/effects/ScreenShake';

/** Static class palettes - avoids per-frame object allocation */
const CLASS_PALETTES: Record<string, {
  skin: string; skinLight: string; skinShadow: string;
  hair: string; hairLight: string;
  armor: string; armorLight: string; armorDark: string;
  cloth: string; clothLight: string;
  accent: string; accentLight: string;
}> = {
  alp: {
    skin: '#D4A574', skinLight: '#E8C4A0', skinShadow: '#B8956A',
    hair: '#2C1810', hairLight: '#4A3020',
    armor: '#5C4033', armorLight: '#8B6914', armorDark: '#3D2B1F',
    cloth: '#8B0000', clothLight: '#CD5C5C',
    accent: '#DAA520', accentLight: '#FFD700',
  },
  kam: {
    skin: '#C9A882', skinLight: '#E0C8A8', skinShadow: '#A08060',
    hair: '#1C1C1C', hairLight: '#3C3C3C',
    armor: '#2E1A47', armorLight: '#4B2D73', armorDark: '#1A0D2E',
    cloth: '#4A3B6B', clothLight: '#7B68AE',
    accent: '#00CED1', accentLight: '#7FFFD4',
  },
  mergen: {
    skin: '#D4A574', skinLight: '#E8C4A0', skinShadow: '#B8956A',
    hair: '#654321', hairLight: '#8B7355',
    armor: '#2F4F2F', armorLight: '#4A7C4A', armorDark: '#1C3A1C',
    cloth: '#6B4423', clothLight: '#A0522D',
    accent: '#FF8C00', accentLight: '#FFB347',
  },
};

/** Pre-allocated sound arrays – avoids per-call array creation */
const FOOTSTEP_SOUNDS = ['footstep_grass', 'footstep_grass_1', 'footstep_grass_2'];
const ATTACK_SOUNDS = ['slash', 'slash_1', 'hit_physical'];

/** Pre-computed 1/sqrt(2) for diagonal normalization (inputs are always -1/0/1) */
const INV_SQRT2 = 1 / Math.sqrt(2);

export interface PlayerConfig {
  x: number;
  y: number;
  className: ClassName;
}

export class Player extends Entity {
  public className: ClassName;
  public level: number = 1;
  public experience: number = 0;
  public skillPoints: number = 0;

  override getLevel(): number {
    return this.level;
  }

  // Inventory
  public inventory: Inventory = new Inventory();

  // Potion belt (consumable system)
  public potionBelt: PotionBelt = new PotionBelt();

  // Currency stash (new crafting system)
  public currencyStash: Map<string, number> = new Map();

  // Skills (new customizable system)
  public customSkills: CustomSkill[] = [];
  public customSkillsV2: CustomSkillV2[] = [];  // V2 skills
  public skillHotbar: SkillHotbar = new SkillHotbar();
  public activeSkill: HotbarSkill | null = null;
  public activeSkillV2: CustomSkillV2 | null = null;

  // V2 Mana Systems
  public reservedMana: number = 0;           // Total % mana reserved by active auras
  public reservedManaFlat: number = 0;       // Total flat mana reserved by active minions
  public activeAuras: Set<string> = new Set(); // Active aura skill IDs
  public activeChannels: Map<string, CustomSkillV2> = new Map(); // Active beam channels

  // Aura bonus cache (avoid Map allocation per damage calc)
  private _auraBonusCache: Map<import('@shared/types').ElementType, number> = new Map();
  private _auraBonusDirty: boolean = true;

  // Passive tree
  public passiveTree: PassiveTreeManager = new PassiveTreeManager();

  // Stat calculator for modifier aggregation
  private statCalculator: StatCalculator = new StatCalculator();
  private statCalculatorDirty: boolean = true;

  // Cached move speed to avoid per-frame statCalculator.calculate calls
  private _cachedMoveSpeed: number = 100;
  private _statsDirty: boolean = true;

  // Base stats (from class/level, without equipment)
  private baseStats = {
    maxHp: 0,
    maxMana: 0,
    armor: 0,
    evasion: 0,
    strength: 0,
    agility: 0,
    spirit: 0,
  };

  // Trigger effect manager
  private triggerManager: TriggerEffectManager = getTriggerEffectManager();

  // Dodge system
  public maxDodgeCharges: number = COMBAT.MAX_DODGE_CHARGES;
  public dodgeCharges: number = COMBAT.MAX_DODGE_CHARGES;
  public dodgeRechargeTimer: number = 0;
  public isDodging: boolean = false;
  public dodgeTimer: number = 0;
  public dodgeDirection: Vector2 = { x: 0, y: 0 };

  // Attack
  public attackRange: number = 60;
  public attackDamage: number = 25;
  public attackSpeed: number = 2.0; // attacks per second

  // Visual feedback
  public hitFlashTimer: number = 0;
  public hitFlashDuration: number = 0.15; // 150ms white-to-red flash

  // Energy Shield recharge delay (2s after taking damage)
  private esRechargeDelay: number = 0;

  // Knockback
  private knockbackVelocity: Vector2 = { x: 0, y: 0 };

  // Footstep sound timer
  private footstepTimer: number = 0;
  private footstepInterval: number = 0.35; // seconds between footstep sounds

  // Elemental Resistances
  public resistances: ElementalResistances = {
    physical: 0, // Physical
    gok: 0,   // Gök (Sky/Lightning)
    od: 0,    // Od (Fire)
    su: 0,    // Su (Water/Ice)
    karanlik: 0, // Karanlık (Dark/Chaos)
  };

  // Pre-rendered frame cache: caches procedural render results as offscreen canvases.
  // Key format: "dir_state_phase" -> offscreen canvas
  // Dramatically reduces redundant gradient + path operations.
  private _frameCache = new Map<string, HTMLCanvasElement>();
  private _frameCacheZoom = 0;
  private _frameCacheEquipHash = '';
  private _cachedEquipHash: string = '';
  private _equipHashDirty: boolean = true;
  private _frameCacheCanvas: HTMLCanvasElement | null = null; // reusable offscreen canvas
  private _frameCacheCtx: CanvasRenderingContext2D | null = null;
  private static readonly FRAME_CACHE_W = 120;  // px at zoom 1 (scaled by zoom)
  private static readonly FRAME_CACHE_H = 140;

  // LPC sprite rendering
  private _lpcRenderer: LPCSpriteRenderer | null = null;
  private _lpcLayers: LPCSpriteLayer[] = [];
  private _lpcLayersDirty = true;
  // Animation state tracking for LPC
  private _lpcPrevAnimState: GameAnimState = 'idle';
  private _lpcAnimStartTime = 0;
  // Dodge ghost trail: stores recent screen positions during dodge
  private _dodgeGhosts: { x: number; y: number; time: number; facing: Entity['facing'] }[] = [];

  // Reference to map for collision
  private map: IsometricMap | null = null;

  constructor(config: PlayerConfig) {
    const entityConfig: EntityConfig = {
      x: config.x,
      y: config.y,
      width: 32,
      height: 48,
      speed: COMBAT.PLAYER_BASE_SPEED,
      stats: {
        hp: BASE_STATS.hp,
        maxHp: BASE_STATS.hp,
        mana: BASE_STATS.mana,
        maxMana: BASE_STATS.mana,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: COMBAT.PLAYER_BASE_ARMOR,
        evasion: COMBAT.PLAYER_BASE_EVASION,
        strength: BASE_STATS.strength,
        agility: BASE_STATS.agility,
        spirit: BASE_STATS.spirit,
      },
    };

    super(entityConfig);
    this.className = config.className;

    // Store base stats (will be modified by level)
    this.baseStats = {
      maxHp: BASE_STATS.hp,
      maxMana: BASE_STATS.mana,
      armor: COMBAT.PLAYER_BASE_ARMOR,
      evasion: COMBAT.PLAYER_BASE_EVASION,
      strength: BASE_STATS.strength,
      agility: BASE_STATS.agility,
      spirit: BASE_STATS.spirit,
    };

    // Initialize class skills
    this.initializeSkills();

    // Initialize passive tree from JSON file (replaces hardcoded tree)
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/data/passive_tree.json', false);
      xhr.send();
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        if (Array.isArray(data) && data.length > 0) {
          this.passiveTree.loadFromJSON(data);
          console.log(`[PassiveTree] Loaded ${data.length} nodes from JSON`);
        }
      }
    } catch (e) {
      console.log('[PassiveTree] Using hardcoded tree');
    }
    // Start with 0 passive points. Lv10 gives 10 points + 1 branch point.
    this.passiveTree.setAvailablePoints(0);
    // TEST: give 2 branch points + 50 passive points for testing
    this.passiveTree.addBranchPoint();
    this.passiveTree.addBranchPoint();
    this.passiveTree.setAvailablePoints(50);

    // TEST: give 100 of each currency for testing
    const testCurrencies = ['ruh_tozu', 'tengri_ozu', 'erlik_tasi', 'arindirma_suyu', 'ruh_muhru', 'yikim_tozu', 'tamga_muhru', 'tengri_duasi'];
    for (const c of testCurrencies) this.currencyStash.set(c, 100);

    // Give starting equipment
    this.initializeStartingEquipment();

    // Give starting effect stones
    this.initializeEffectStones();

    // Give starting potions
    this.potionBelt.assignPotion(0, 'hp_small', 5);
    this.potionBelt.assignPotion(1, 'mana_small', 5);

    // Apply equipment stats
    this.recalculateStats();
  }

  private initializeStartingEquipment(): void {
    // Class-specific starting weapons (using new registry IDs)
    const classWeapons: Record<string, string> = {
      alp: 'weapon_sword_1',
      kam: 'weapon_staff_1',
      mergen: 'weapon_bow_1',
    };

    // Create and equip starting weapon
    const weaponBase = classWeapons[this.className] || 'weapon_sword_1';
    const weapon = createEquipmentItem(weaponBase, 'normal', 1);
    if (weapon) {
      this.inventory.equipItem(weapon);
    }

    // Create and equip basic armor
    const chest = createEquipmentItem('chest_ev_1', 'normal', 1);
    if (chest) {
      this.inventory.equipItem(chest);
    }

    // Create and equip boots
    const boots = createEquipmentItem('boots_ev_1', 'normal', 1);
    if (boots) {
      this.inventory.equipItem(boots);
    }

    // Create and equip gloves
    const gloves = createEquipmentItem('gloves_ev_1', 'normal', 1);
    if (gloves) {
      this.inventory.equipItem(gloves);
    }

    // Create and equip belt
    const belt = createEquipmentItem('belt_ar_1', 'normal', 1);
    if (belt) {
      this.inventory.equipItem(belt);
    }
  }

  private initializeSkills(): void {
    // Initialize starter skills for this class using the new customizable system
    this.customSkills = initializeStarterSkills(this.className);

    // Create V2 starting skills
    // Skill 1: Melee Thrust (max values)
    const thrust = SkillCreatorV2.createSkill('melee', 'physical', 'Hamle');
    thrust.meleeType = 'thrust';
    (thrust.sliders as any).damage = 200;  // max damage %
    (thrust.sliders as any).thrustRange = 250; // max thrust range
    SkillCreatorV2.recalculateCosts(thrust);
    this.customSkillsV2.push(thrust);
    this.skillHotbar.assign(thrust, 0);

    // Skill 2: Warcry buff
    const warcry = SkillCreatorV2.createSkill('buff', 'physical', 'Savaş Çığlığı');
    warcry.buffType = 'warcry';
    SkillCreatorV2.recalculateCosts(warcry);
    this.customSkillsV2.push(warcry);
    this.skillHotbar.assign(warcry, 1);
  }

  private initializeEffectStones(): void {
    // Add all starter effect stones to inventory
    const stones = initializeStarterEffectStones();
    for (const stone of stones) {
      this.inventory.addEffectStone(stone);
    }
  }

  setMap(map: IsometricMap): void {
    this.map = map;
  }

  handleInput(input: InputManager, deltaTime: number): void {
    if (!this.isAlive) return;

    // Handle dodge
    if (input.isKeyPressed('Space') && this.dodgeCharges > 0 && !this.isDodging) {
      this.startDodge(input);
    }

    // Handle movement (if not dodging or attacking)
    if (!this.isDodging && !this.isAttacking && this.activeChannels.size === 0) {
      const movement = input.getMovementVector();
      this.moveWithCollision(movement.x, movement.y, deltaTime);
    } else if (this.isAttacking || this.activeChannels.size > 0) {
      // Stop movement during attack/channeling
      this.velocity.x = 0;
      this.velocity.y = 0;
    }

    // Note: Skill and attack handling is done in Game.ts update method
  }

  /**
   * Move with collision detection - checks map boundaries and prop collision
   */
  private moveWithCollision(dx: number, dy: number, deltaTime: number): void {
    if (!this.isAlive) return;
    if (dx === 0 && dy === 0) {
      this.velocity.x = 0;
      this.velocity.y = 0;
      this.footstepTimer = 0;
      return;
    }

    // Play footstep sounds
    this.footstepTimer += deltaTime;
    if (this.footstepTimer >= this.footstepInterval) {
      this.footstepTimer = 0;
      getAudioManager().play(FOOTSTEP_SOUNDS[Math.floor(Math.random() * FOOTSTEP_SOUNDS.length)]);
    }

    // Normalize diagonal movement (inputs are always -1/0/1, so length is always sqrt(2))
    if (dx !== 0 && dy !== 0) {
      dx *= INV_SQRT2;
      dy *= INV_SQRT2;
    }

    // Apply terrain speed modifier (water, mud, hills slow you down)
    const terrainMult = this.map ? this.map.getMovementSpeedMultiplier(this.position.x, this.position.y) : 1.0;
    if (this._statsDirty) {
      this._cachedMoveSpeed = this.statCalculator.calculate('moveSpeed', 100);
      this._statsDirty = false;
    }
    const moveSpeedMult = this._cachedMoveSpeed / 100;
    const effectiveSpeed = this.speed * terrainMult * moveSpeedMult;

    const velocityX = dx * effectiveSpeed * deltaTime;
    const velocityY = dy * effectiveSpeed * deltaTime;

    // Calculate new position
    const newX = this.position.x + velocityX;
    const newY = this.position.y + velocityY;

    // Check if new position is walkable
    if (this.map && this.map.isWorldPositionWalkable(newX, newY)) {
      // Full movement allowed
      this.position.x = newX;
      this.position.y = newY;
      this.velocity.x = dx * effectiveSpeed;
      this.velocity.y = dy * effectiveSpeed;
    } else if (this.map) {
      // Try sliding along walls - check X and Y separately
      let movedX = false;
      let movedY = false;

      // Try X movement only
      if (this.map.isWorldPositionWalkable(newX, this.position.y)) {
        this.position.x = newX;
        movedX = true;
      }

      // Try Y movement only
      if (this.map.isWorldPositionWalkable(this.position.x, newY)) {
        this.position.y = newY;
        movedY = true;
      }

      this.velocity.x = movedX ? dx * effectiveSpeed : 0;
      this.velocity.y = movedY ? dy * effectiveSpeed : 0;
    } else {
      // No map reference, allow free movement
      this.position.x = newX;
      this.position.y = newY;
      this.velocity.x = dx * effectiveSpeed;
      this.velocity.y = dy * effectiveSpeed;
    }

    // Update facing direction (8-directional)
    if (dx > 0 && dy > 0) this.facing = 'down-right';
    else if (dx > 0 && dy < 0) this.facing = 'up-right';
    else if (dx < 0 && dy > 0) this.facing = 'down-left';
    else if (dx < 0 && dy < 0) this.facing = 'up-left';
    else if (dx > 0) this.facing = 'right';
    else if (dx < 0) this.facing = 'left';
    else if (dy > 0) this.facing = 'down';
    else if (dy < 0) this.facing = 'up';
  }

  private startDodge(input: InputManager): void {
    const movement = input.getMovementVector();

    // Use movement direction, or facing direction if not moving
    if (movement.x !== 0 || movement.y !== 0) {
      this.dodgeDirection = movement;
    } else {
      const fv = this.getFacingVector();
      this.dodgeDirection = { x: fv.x, y: fv.y };
    }

    this.isDodging = true;
    this.dodgeTimer = COMBAT.DODGE_I_FRAMES;
    this.dodgeCharges--;
    this.setInvincible(COMBAT.DODGE_I_FRAMES);
  }

  attack(): { x: number; y: number; damage: number } | null {
    if (this.isAttacking || this.attackCooldown > 0) return null;

    this.isAttacking = true;
    const attackSpeedMult = this.statCalculator.calculate('attackSpeed', 100) / 100;
    this.attackCooldown = 1 / (this.attackSpeed * attackSpeedMult);

    // Play attack sound
    const audio = getAudioManager();
    audio.play(ATTACK_SOUNDS[Math.floor(Math.random() * ATTACK_SOUNDS.length)]);

    // Return attack info for combat system
    const facingVec = this.getFacingVector();
    return {
      x: this.position.x + facingVec.x * this.attackRange,
      y: this.position.y + facingVec.y * this.attackRange,
      damage: this.attackDamage,
    };
  }

  useSkill(slot: number): { success: boolean; skill: HotbarSkill | null; damage: number; isV2: boolean } {
    // Check if silenced - cannot use skills
    if (this.isSilenced) {
      return { success: false, skill: null, damage: 0, isV2: false };
    }

    // Global AOE cooldown - reject AoE casts while on cooldown
    const skillToUse = this.skillHotbar.get(slot);
    if (skillToUse) {
      // Each AOE skill has its own cooldown, no global AOE cooldown needed

      // Weapon type validation for V1 skills
      if (!isSkillV2(skillToUse)) {
        const v1Skill = skillToUse as CustomSkill;
        const weapon = this.inventory.getEquippedItem('weapon');
        const weaponType = getWeaponTypeFromName(weapon?.baseName || weapon?.name);
        if (!isSkillAllowedForWeapon(v1Skill.baseType, weaponType)) {
          return { success: false, skill: skillToUse, damage: 0, isV2: false };
        }
      }
    }

    // Check if Blood Magic is active - use HP instead of mana
    const useBloodMagic = this.hasBloodMagic();
    const resourcePool = useBloodMagic ? this.stats.hp : this.stats.mana;

    // Blood Magic reduces mana cost by 30%
    const costMultiplier = useBloodMagic ? COMBAT.BLOOD_MAGIC_COST_MULT : 1.0;

    // Calculate cooldown reduction from stats (capped) and cast speed
    const cdr = Math.min(this.statCalculator.calculate('cooldownReduction', 0), COMBAT.MAX_CDR_PERCENT);
    const castSpeedMult = this.statCalculator.calculate('castSpeed', 100) / 100;
    const cooldownMultiplier = (1 - cdr / 100) / Math.max(castSpeedMult, COMBAT.MIN_CAST_SPEED_MULT);

    // Attack speed cooldown for melee/projectile skills (uses attack speed instead of fixed cooldown)
    const attackSpeedMult = this.statCalculator.calculate('attackSpeed', 100) / 100;
    const atkSpeedCd = 1 / (this.attackSpeed * attackSpeedMult);

    const result = this.skillHotbar.use(slot, resourcePool / costMultiplier, cooldownMultiplier, atkSpeedCd);

    if (result.success && result.skill) {
      const actualCost = Math.floor(result.manaCost * costMultiplier);

      // Deduct from appropriate resource
      if (useBloodMagic) {
        // Blood Magic: deduct from HP (but never kill player)
        this.stats.hp = Math.max(1, this.stats.hp - actualCost);
      } else {
        // Normal: deduct from mana
        this.stats.mana -= actualCost;
      }

      // Set active skill for visual feedback
      this.activeSkill = result.skill;
      this.isAttacking = true;
      // Animation duration by skill type:
      // Melee/projectile: attack speed based
      // Spells (aoe, curse, trap, totem, buff): cast speed based
      // Aura/minion/beam: instant (toggle/channel)
      if (result.isV2) {
        const dt = (result.skill as CustomSkillV2).deliveryType;
        if (dt === 'melee' || dt === 'projectile') {
          this.attackCooldown = atkSpeedCd * 0.8;
        } else if (dt === 'aoe' || dt === 'curse' || dt === 'trap' || dt === 'totem' || dt === 'buff') {
          // Cast time from skill slider, reduced by cast speed stat
          const baseCastTime = ((result.skill as CustomSkillV2).sliders as any)?.castTime || 0.8;
          this.attackCooldown = baseCastTime / Math.max(castSpeedMult, COMBAT.MIN_CAST_SPEED_MULT);
        } else {
          this.attackCooldown = COMBAT.SKILL_ANIMATION_COOLDOWN;
        }
      } else {
        this.attackCooldown = COMBAT.SKILL_ANIMATION_COOLDOWN;
      }

      // Global AOE cooldown removed - each skill uses its own cooldown

      // Calculate skill damage based on V1 or V2
      let damage: number;
      if (result.isV2) {
        const v2 = result.skill as CustomSkillV2;
        if (v2.deliveryType === 'melee' || v2.deliveryType === 'projectile') {
          // Melee/Projectile: damage slider is % of player's attack damage
          const damagePct = (v2.sliders as any).damage / 100;
          damage = Math.floor(this.attackDamage * damagePct);
        } else {
          // Other V2: getEffectiveDamage returns flat damage value
          damage = Math.floor(SkillCreatorV2.getEffectiveDamage(v2));
        }
      } else {
        // V1: getEffectiveDamage returns a multiplier
        damage = Math.floor(this.attackDamage * SkillCreator.getEffectiveDamage(result.skill as CustomSkill));
      }

      // Grant skill XP on use (1 XP per use)
      if (result.isV2) {
        SkillCreatorV2.addXP(result.skill as CustomSkillV2, 1);
      } else {
        SkillCreator.addXP(result.skill as CustomSkill, 1);
      }

      // Process on_skill_use trigger
      getTriggerEffectManager().processTrigger('on_skill_use', this.statCalculator, {
        skillBaseType: result.isV2 ? (result.skill as CustomSkillV2).deliveryType : 'aoe',
        skillId: result.skill.id,
      });

      return { success: true, skill: result.skill, damage, isV2: result.isV2 };
    }

    return { success: false, skill: result.skill, damage: 0, isV2: result.isV2 };
  }

  /**
   * Check if a skill is an AOE type skill
   */
  // Global AOE cooldown removed - each AOE skill uses its own cooldown
  getGlobalAoeCooldownPercent(): number {
    return 0;
  }

  update(deltaTime: number): void {
    this.updateTimers(deltaTime);

    // Update hit flash
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= deltaTime;
    }

    // Apply knockback
    if (Math.abs(this.knockbackVelocity.x) > COMBAT.KNOCKBACK_THRESHOLD || Math.abs(this.knockbackVelocity.y) > COMBAT.KNOCKBACK_THRESHOLD) {
      const kbDecay = COMBAT.KNOCKBACK_DECAY_RATE;
      this.knockbackVelocity.x *= Math.max(0, 1 - kbDecay * deltaTime);
      this.knockbackVelocity.y *= Math.max(0, 1 - kbDecay * deltaTime);

      const newX = this.position.x + this.knockbackVelocity.x * deltaTime;
      const newY = this.position.y + this.knockbackVelocity.y * deltaTime;

      if (this.map && this.map.isWorldPositionWalkable(newX, newY)) {
        this.position.x = newX;
        this.position.y = newY;
      }
    }

    // Update skill cooldowns
    this.skillHotbar.update(deltaTime);

    // Update potion belt cooldowns
    this.potionBelt.update(deltaTime);

    // Global AOE cooldown removed

    // Clear active skill after animation
    if (!this.isAttacking) {
      this.activeSkill = null;
    }

    // Update dodge with collision
    if (this.isDodging) {
      const dodgeSpeed = this.speed * COMBAT.DODGE_SPEED_MULTIPLIER;
      const newX = this.position.x + this.dodgeDirection.x * dodgeSpeed * deltaTime;
      const newY = this.position.y + this.dodgeDirection.y * dodgeSpeed * deltaTime;

      // Check collision during dodge
      if (this.map && this.map.isWorldPositionWalkable(newX, newY)) {
        this.position.x = newX;
        this.position.y = newY;
      } else if (this.map) {
        // Try partial movement
        if (this.map.isWorldPositionWalkable(newX, this.position.y)) {
          this.position.x = newX;
        }
        if (this.map.isWorldPositionWalkable(this.position.x, newY)) {
          this.position.y = newY;
        }
      } else {
        this.position.x = newX;
        this.position.y = newY;
      }

      this.dodgeTimer -= deltaTime;
      if (this.dodgeTimer <= 0) {
        this.isDodging = false;
      }
    }

    // Recharge dodge
    if (this.dodgeCharges < this.maxDodgeCharges) {
      this.dodgeRechargeTimer += deltaTime;
      if (this.dodgeRechargeTimer >= COMBAT.DODGE_RECHARGE_TIME) {
        this.dodgeCharges++;
        this.dodgeRechargeTimer = 0;
      }
    }

    // Note: Map boundary collision is now handled in moveWithCollision()

    // Process buffs (regen, shield, etc.)
    const buffResult = this.updateBuffs(deltaTime);
    // Buff changes affect stat calculator (damage boost, etc.)
    if (this.buffManager.getActiveBuffs().length > 0 || buffResult.expiredBuffs.length > 0) {
      this.statCalculatorDirty = true;
    }

    // Show floating text when important buffs expire
    if (buffResult.expiredBuffs.length > 0) {
      for (const buffName of buffResult.expiredBuffs) {
        getDamageNumbers().spawn(this.position.x, this.position.y - 30, `${buffName} ended`, 'miss');
      }
    }

    // Apply HP regeneration from buffs + items
    const itemHpRegen = this.statCalculator.calculate('hpRegen', 0);
    const totalHpRegen = buffResult.hpRegen + itemHpRegen;
    if (totalHpRegen > 0 && this.stats.hp < this.stats.maxHp) {
      this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + totalHpRegen * deltaTime);
    }

    // Update V2 channeling (drains mana while channeling beams)
    this.updateChanneling(deltaTime);

    // Update V2 skill cooldowns
    for (const skill of this.customSkillsV2) {
      SkillCreatorV2.update(skill, deltaTime);
    }

    // Mana regeneration (base + buff + items)
    const itemManaRegen = this.statCalculator.calculate('manaRegen', 0);
    let manaRegen = COMBAT.BASE_MANA_REGEN + buffResult.manaRegen + itemManaRegen;

    // Calculate effective max mana (after aura reserves)
    const effectiveMaxMana = this.getEffectiveMaxMana();

    if (this.stats.mana < effectiveMaxMana) {
      this.stats.mana = Math.min(
        effectiveMaxMana,
        this.stats.mana + manaRegen * deltaTime
      );
    }

    // If mana exceeds effective max (aura was just activated), cap it
    if (this.stats.mana > effectiveMaxMana) {
      this.stats.mana = effectiveMaxMana;
    }

    // Energy Shield regeneration (20% per second after 2s without taking damage)
    if (this.stats.maxEnergyShield > 0 && this.stats.energyShield < this.stats.maxEnergyShield) {
      if (this.esRechargeDelay <= 0) {
        this.stats.energyShield = Math.min(
          this.stats.maxEnergyShield,
          this.stats.energyShield + this.stats.maxEnergyShield * 0.2 * deltaTime
        );
      } else {
        this.esRechargeDelay -= deltaTime;
      }
    }

    // Process status effects (DoT damage)
    const dotDamage = this.updateStatusEffects(deltaTime);
    if (dotDamage > 0) {
      this.takeDamage(dotDamage);
    }

    // If buffs changed, trigger stat recalculation
    if (buffResult.expiredBuffs.length > 0) {
      this.recalculateStats();
    }
  }

  // ========================
  // V2 Mana System Methods
  // ========================

  /**
   * Get available mana (current mana that can be spent)
   */
  getAvailableMana(): number {
    return this.stats.mana;
  }

  /**
   * Get effective max mana after aura reserves
   */
  getEffectiveMaxMana(): number {
    const reservedAmount = (this.stats.maxMana * this.reservedMana) / 100;
    return Math.max(0, Math.floor(this.stats.maxMana - reservedAmount - this.reservedManaFlat));
  }

  /**
   * Get health percentage (0-1)
   */
  getHealthPercent(): number {
    return this.stats.maxHp > 0 ? this.stats.hp / this.stats.maxHp : 0;
  }

  /**
   * Get mana percentage (0-1) based on effective max
   */
  getManaPercent(): number {
    const effectiveMax = this.getEffectiveMaxMana();
    return effectiveMax > 0 ? this.stats.mana / effectiveMax : 0;
  }

  /**
   * Apply a potion effect from the consumable system
   */
  applyPotionEffect(effect: PotionEffect): void {
    if (effect.type === 'instant_heal') {
      const healAmount = effect.isPercent
        ? Math.floor(this.stats.maxHp * effect.value / 100)
        : effect.value;
      if (healAmount > 0) {
        this.heal(healAmount);
      }
    } else if (effect.type === 'instant_mana') {
      const manaAmount = effect.isPercent
        ? Math.floor(this.stats.maxMana * effect.value / 100)
        : effect.value;
      if (manaAmount > 0) {
        this.stats.mana = Math.min(this.stats.maxMana, this.stats.mana + manaAmount);
      }
    } else if (effect.type === 'buff' && effect.duration && effect.buffStat) {
      // Map potion buff stats to existing buff template IDs
      const buffStatToTemplate: Record<string, string> = {
        hpRegen: 'hp_regen',
        movementSpeed: 'speed_boost',
        damage: 'damage_boost',
        allResistance: 'resistance_boost',
        damageReduction: 'damage_reduction',
      };
      const templateId = buffStatToTemplate[effect.buffStat];
      if (templateId) {
        this.applyBuff(templateId, 'potion_' + effect.buffStat, 'potion');
      }
    }
    // Handle cleanse
    if (effect.cleanse) {
      for (const effectName of effect.cleanse) {
        this.removeStatusEffect(effectName as import('@shared/effects/StatusEffect').StatusType);
      }
    }
  }

  /**
   * Toggle an aura skill on/off
   */
  toggleAura(skill: CustomSkillV2): boolean {
    if (skill.deliveryType !== 'aura') return false;

    const reservePercent = skill.manaReservePercent || 20;

    if (this.activeAuras.has(skill.id)) {
      // Deactivate aura
      this.activeAuras.delete(skill.id);
      this.reservedMana = Math.max(0, this.reservedMana - reservePercent);
      this._auraBonusDirty = true;
      skill.isActive = false;
      return true;
    } else {
      // Activate aura - check if we have enough mana
      const newReservedTotal = this.reservedMana + reservePercent;

      // Hard cap at 95% mana reserve to prevent breaking calculations
      if (newReservedTotal > 95) {
        return false; // Cannot reserve more than 95% mana
      }

      const newEffectiveMax = (this.stats.maxMana * (100 - newReservedTotal)) / 100;

      if (this.stats.mana <= 0) {
        return false; // Can't activate with no mana
      }

      this.activeAuras.add(skill.id);
      this.reservedMana = newReservedTotal;
      this._auraBonusDirty = true;
      skill.isActive = true;

      // Cap current mana to new effective max
      if (this.stats.mana > newEffectiveMax) {
        this.stats.mana = Math.floor(newEffectiveMax);
      }

      return true;
    }
  }

  /**
   * Get damage bonuses from active auras
   * Returns a map of element type to damage multiplier bonus (e.g., 0.25 = +25% damage)
   */
  getAuraBonuses(): Map<import('@shared/types').ElementType, number> {
    if (!this._auraBonusDirty) return this._auraBonusCache;

    this._auraBonusCache.clear();

    // Check all active auras
    for (const skillId of this.activeAuras) {
      // Find the skill in V2 skills
      const skill = this.customSkillsV2.find(s => s.id === skillId);
      if (!skill || skill.deliveryType !== 'aura') continue;

      // Get effect strength multiplier (1.05 - 1.25 from 5-25% slider)
      const effectStrength = SkillCreatorV2.getEffectStrength(skill);

      // effectStrength is already a multiplier (e.g. 1.10 = +10% bonus)
      // Convert to damage bonus: subtract 1 to get the bonus portion
      const damageBonus = effectStrength - 1.0;

      // Aura boosts damage of its own element
      const element = skill.element;
      const currentBonus = this._auraBonusCache.get(element) || 0;
      this._auraBonusCache.set(element, currentBonus + damageBonus);

      // Physical auras also provide a small all-damage bonus
      if (element === 'physical') {
        // Add small bonus to all elements
        const allElements: import('@shared/types').ElementType[] = ['od', 'gok', 'su', 'karanlik'];
        for (const elem of allElements) {
          const current = this._auraBonusCache.get(elem) || 0;
          this._auraBonusCache.set(elem, current + damageBonus * 0.2); // 20% of physical bonus
        }
      }
    }

    this._auraBonusDirty = false;
    return this._auraBonusCache;
  }

  /**
   * Get total aura damage bonus for a specific element
   */
  getAuraDamageBonus(element: import('@shared/types').ElementType): number {
    return this.getAuraBonuses().get(element) || 0;
  }

  /**
   * Start channeling a beam skill
   */
  startChanneling(skill: CustomSkillV2): boolean {
    if (skill.deliveryType !== 'beam') return false;
    if (this.activeChannels.has(skill.id)) return false;

    const drainRate = skill.manaDrainPerSecond || 15;
    if (this.stats.mana < drainRate * 0.5) {
      return false; // Need some mana to start
    }

    this.activeChannels.set(skill.id, skill);
    skill.isActive = true;
    return true;
  }

  /**
   * Stop channeling a beam skill
   */
  stopChanneling(skillId: string): boolean {
    const skill = this.activeChannels.get(skillId);
    if (!skill) return false;

    this.activeChannels.delete(skillId);
    skill.isActive = false;
    return true;
  }

  /**
   * Stop all channeling (e.g., when stunned or player releases key)
   */
  stopAllChanneling(): void {
    for (const [, skill] of this.activeChannels) {
      skill.isActive = false;
    }
    this.activeChannels.clear();
  }

  /**
   * Update channeling - drain mana while channeling
   */
  private updateChanneling(deltaTime: number): void {
    if (this.activeChannels.size === 0) return;

    const toRemove: string[] = [];

    for (const [skillId, skill] of this.activeChannels) {
      const drainRate = skill.manaDrainPerSecond || 15;
      const manaDrain = drainRate * deltaTime;

      if (this.stats.mana >= manaDrain) {
        this.stats.mana -= manaDrain;
      } else {
        // Out of mana - stop channeling
        this.stats.mana = 0;
        skill.isActive = false;
        toRemove.push(skillId);
      }
    }

    // Remove channels that ran out of mana
    for (const skillId of toRemove) {
      this.activeChannels.delete(skillId);
    }
  }

  /**
   * Check if a V2 skill can be used
   */
  canUseSkillV2(skill: CustomSkillV2): boolean {
    return SkillCreatorV2.canUse(skill, this.stats.mana, this.stats.maxMana);
  }

  /**
   * Use a V2 skill (returns true if successful)
   */
  useSkillV2(skill: CustomSkillV2): { success: boolean; damage: number } {
    if (!this.canUseSkillV2(skill)) {
      return { success: false, damage: 0 };
    }

    // Handle different delivery types
    switch (skill.deliveryType) {
      case 'aura':
        return { success: this.toggleAura(skill), damage: 0 };

      case 'beam':
        if (!skill.isActive) {
          return { success: this.startChanneling(skill), damage: 0 };
        } else {
          this.stopChanneling(skill.id);
          return { success: true, damage: 0 };
        }

      default:
        // Normal skill - deduct mana and start cooldown
        this.stats.mana -= skill.manaCost;
        SkillCreatorV2.use(skill);
        const damage = SkillCreatorV2.getEffectiveDamage(skill);
        return { success: true, damage };
    }
  }

  /** Initialize LPC sprite renderer (called lazily on first render) */
  initLPCRenderer(assetLoader: import('@client/core/AssetLoader').AssetLoader): void {
    if (!this._lpcRenderer) {
      this._lpcRenderer = new LPCSpriteRenderer(assetLoader);
      this._lpcLayersDirty = true;
    }
  }

  /** Rebuild LPC sprite layers from current equipment */
  private rebuildLPCLayers(): void {
    const chest = this.inventory.getEquippedItem('chest');
    const head = this.inventory.getEquippedItem('head');
    const weapon = this.inventory.getEquippedItem('weapon');
    const boots = this.inventory.getEquippedItem('boots');
    const gloves = this.inventory.getEquippedItem('gloves');
    const cape = this.inventory.getEquippedItem('cape');
    const belt = this.inventory.getEquippedItem('belt');
    const offhand = this.inventory.getEquippedItem('offhand');

    this._lpcLayers = LPCSpriteRenderer.buildLayersForPlayer({
      className: this.className,
      chestStyle: chest?.visual?.style,
      chestTint: chest?.visual?.primaryColor,
      headEquipped: !!head,
      headTint: head?.visual?.primaryColor,
      weaponName: weapon?.baseName || weapon?.name,
      weaponTint: weapon?.visual?.primaryColor,
      bootsEquipped: !!boots,
      bootsTint: boots?.visual?.primaryColor,
      glovesEquipped: !!gloves,
      glovesTint: gloves?.visual?.primaryColor,
      capeEquipped: !!cape,
      capeTint: cape?.visual?.primaryColor,
      beltEquipped: !!belt,
      offhandEquipped: !!offhand,
      // Defense type → sprite variant
      bootsDefense: (boots as EquipmentItem | undefined)?.defenseType,
      glovesDefense: (gloves as EquipmentItem | undefined)?.defenseType,
      headDefense: (head as EquipmentItem | undefined)?.defenseType,
      chestDefense: (chest as EquipmentItem | undefined)?.defenseType,
      // Rarity → cape/robe color
      capeRarity: cape?.rarity,
      chestRarity: chest?.rarity,
    });
    this._lpcLayersDirty = false;
  }

  /** Render character preview for UI (equipment screen etc.) */
  renderPreview(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): boolean {
    if (!this._lpcRenderer) return false;
    if (this._lpcLayersDirty) {
      this.rebuildLPCLayers();
      this._lpcRenderer.clearCache();
    }
    if (!this._lpcRenderer.areLayersLoaded(this._lpcLayers)) return false;
    // Draw idle pose facing down (front-facing) at given position
    return this._lpcRenderer.drawComposite(ctx, this._lpcLayers, 'idle', 'down', 0, x + w / 2, y + h, w, h);
  }

  /** Equipment visual hash for cache invalidation (dirty-flag cached) */
  private getEquipVisualHash(): string {
    if (!this._equipHashDirty) return this._cachedEquipHash;
    const w = this.inventory.getEquippedItem('weapon');
    const c = this.inventory.getEquippedItem('chest');
    const b = this.inventory.getEquippedItem('boots');
    const g = this.inventory.getEquippedItem('gloves');
    const h = this.inventory.getEquippedItem('head');
    const cp = this.inventory.getEquippedItem('cape');
    const bt = this.inventory.getEquippedItem('belt');
    this._cachedEquipHash = `${this.className}_${w?.id || 0}_${c?.id || 0}_${b?.id || 0}_${g?.id || 0}_${h?.id || 0}_${cp?.id || 0}_${bt?.id || 0}`;
    this._equipHashDirty = false;
    return this._cachedEquipHash;
  }

  /** Render status effect visual overlays on the player sprite */
  private renderPlayerStatusOverlays(
    ctx: CanvasRenderingContext2D,
    cx: number, feetY: number,
    sw: number, sh: number,
    z: number, time: number,
  ): void {
    const effects = this.statusEffects.getAll();
    if (effects.length === 0) return;

    const hw = sw * 0.3;
    const centerY = feetY - sh * 0.5;

    for (const effect of effects) {
      switch (effect.type) {
        case 'burn': {
          const flicker = 0.12 + Math.sin(time * 12) * 0.04;
          ctx.fillStyle = `rgba(255, 100, 0, ${flicker})`;
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw, sh * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Rising flame particles
          for (let i = 0; i < 3; i++) {
            const px = cx + (Math.sin(time * 8 + i * 2.1) * hw * 0.7);
            const py = centerY - sh * 0.3 - ((time * 40 + i * 15) % (sh * 0.5));
            const pAlpha = 0.4 + Math.sin(time * 10 + i) * 0.2;
            const pSize = (2 + Math.sin(time * 6 + i) * 1) * z;
            ctx.fillStyle = `rgba(255, ${130 + i * 30}, 0, ${pAlpha})`;
            ctx.beginPath();
            ctx.arc(px, py, pSize, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'poison': {
          const pulse = 0.08 + Math.sin(time * 4) * 0.04;
          ctx.fillStyle = `rgba(50, 200, 50, ${pulse})`;
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw, sh * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Dripping drops
          for (let i = 0; i < 2; i++) {
            const dx = cx + (i === 0 ? -hw * 0.4 : hw * 0.4);
            const dy = centerY + ((time * 25 + i * 20) % (sh * 0.4));
            const dSize = (1.5 + Math.sin(time * 3 + i) * 0.5) * z;
            ctx.fillStyle = `rgba(80, 220, 40, ${0.5 + Math.sin(time * 5 + i) * 0.2})`;
            ctx.beginPath();
            ctx.arc(dx, dy, dSize, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'bleed': {
          const bleedAlpha = 0.1 + Math.sin(time * 6) * 0.04;
          ctx.fillStyle = `rgba(180, 0, 0, ${bleedAlpha})`;
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw, sh * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Falling blood drops (based on stacks)
          const drops = Math.min(effect.stacks || 1, 5);
          for (let i = 0; i < drops; i++) {
            const bx = cx + (Math.sin(i * 1.3) * hw * 0.6);
            const by = centerY + ((time * 30 + i * 12) % (sh * 0.5));
            const bSize = (1.5 + Math.sin(time * 4 + i) * 0.5) * z;
            ctx.fillStyle = `rgba(200, 0, 0, ${0.5 + Math.sin(time * 7 + i) * 0.2})`;
            ctx.beginPath();
            ctx.arc(bx, by, bSize, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'chill': {
          ctx.fillStyle = 'rgba(100, 150, 255, 0.15)';
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw, sh * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 'freeze': {
          ctx.fillStyle = 'rgba(150, 200, 255, 0.3)';
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw + 3 * z, sh * 0.4 + 3 * z, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = 'rgba(180, 220, 255, 0.6)';
          ctx.lineWidth = 2 * z;
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw + 3 * z, sh * 0.4 + 3 * z, 0, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'shock': {
          const sparkAlpha = 0.1 + Math.abs(Math.sin(time * 15)) * 0.12;
          ctx.fillStyle = `rgba(255, 255, 100, ${sparkAlpha})`;
          ctx.beginPath();
          ctx.ellipse(cx, centerY, hw, sh * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Lightning sparks
          for (let i = 0; i < 2; i++) {
            const lx = cx + (Math.sin(time * 20 + i * 3.7) * hw * 0.8);
            const ly = centerY + (Math.cos(time * 18 + i * 2.3) * sh * 0.3);
            const lSize = (1.5 + Math.random() * 1) * z;
            ctx.fillStyle = `rgba(255, 255, 200, ${0.6 + Math.random() * 0.3})`;
            ctx.beginPath();
            ctx.arc(lx, ly, lSize, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
      }
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const z = camera.zoom;
    const facingLeft = this.facing === 'left' || this.facing === 'up-left' || this.facing === 'down-left';
    const facingMult = facingLeft ? -1 : 1;
    const facingUp = this.facing === 'up' || this.facing === 'up-left' || this.facing === 'up-right';
    const verticalOffset = facingUp ? -3 * z : 0;

    // Animation timing
    const time = performance.now() / 1000;
    const isMoving = this.velocity.x !== 0 || this.velocity.y !== 0;
    const walkCycle = isMoving ? time * 10 : 0;
    const breathe = Math.sin(time * 2) * 0.5;

    const isHit = this.hitFlashTimer > 0;
    const baseY = screenPos.y;
    const walkBob = isMoving ? Math.sin(walkCycle * 2) * 1.5 * z : 0;

    // === SHADOW (cached offscreen canvas) ===
    const shadowCanvas = getPlayerShadowCanvas(z);
    const shadowStretch = 1 + (isMoving ? Math.abs(Math.sin(walkCycle)) * 0.1 : 0);
    if (shadowStretch > 1.001) {
      ctx.save();
      ctx.translate(screenPos.x, baseY + 2 * z);
      ctx.scale(shadowStretch, 1);
      ctx.drawImage(shadowCanvas, -shadowCanvas.width / 2, -shadowCanvas.height / 2);
      ctx.restore();
    } else {
      ctx.drawImage(shadowCanvas, screenPos.x - shadowCanvas.width / 2, baseY + 2 * z - shadowCanvas.height / 2);
    }

    // === LPC SPRITE RENDERING (preferred) ===
    if (this._lpcRenderer) {
      // Rebuild layers if equipment changed
      if (this._lpcLayersDirty) {
        this.rebuildLPCLayers();
        this._lpcRenderer.clearCache();
      }

      // Check if minimum layers are loaded (body at least)
      if (this._lpcRenderer.areLayersLoaded(this._lpcLayers)) {
        // Determine animation state; class-specific attack anim
        let lpcAnimState: GameAnimState;
        if (isHit && !this.isAttacking && !isMoving) {
          lpcAnimState = 'hurt';
        } else if (this.isAttacking) {
          if (this.className === 'kam') lpcAnimState = 'cast';
          else if (this.className === 'mergen') lpcAnimState = 'shoot';
          else lpcAnimState = 'attack';
        } else if (isMoving) {
          lpcAnimState = 'walk';
        } else {
          lpcAnimState = 'idle';
        }

        // Track animation transitions for proper start-time-based playback
        if (lpcAnimState !== this._lpcPrevAnimState) {
          this._lpcAnimStartTime = time;
          this._lpcPrevAnimState = lpcAnimState;
        }

        const elapsed = time - this._lpcAnimStartTime;
        const frameCount = this._lpcRenderer.getFrameCount(lpcAnimState);
        let frameIndex: number;
        // No idle bouncing - keep character steady
        const breathOffset = 0;

        if (lpcAnimState === 'idle') {
          // Idle: static pose - no bouncing
          frameIndex = 0;
        } else if (lpcAnimState === 'walk') {
          // Walk: 9 frames looping at ~8 fps
          frameIndex = Math.floor(elapsed * 8) % frameCount;
        } else {
          // Attack/cast/shoot: play once from start, hold last frame
          const animFps = lpcAnimState === 'shoot' ? 12 : 10;
          frameIndex = Math.min(Math.floor(elapsed * animFps), frameCount - 1);
        }

        // Sprite draw size: match procedural character proportions
        // LPC frame is 64x64 but character occupies ~32x50 within that.
        // Scale so character height ≈ entity height (48) * ~2.2 for visual match
        const spriteScale = 1.6;
        const spriteW = 64 * z * spriteScale;
        const spriteH = 64 * z * spriteScale;
        // LPC frames are 64x64 but character feet sit ~15% above the frame bottom.
        // Push the sprite down so feet align with the shadow / ground point.
        const feetOffset = spriteH * 0.15;

        ctx.save();
        // Pixel-art: disable smoothing for crisp sprites
        ctx.imageSmoothingEnabled = false;

        // Dodge ghost trail
        if (this.isDodging) {
          // Record position every ~50ms
          const lastGhost = this._dodgeGhosts[this._dodgeGhosts.length - 1];
          if (!lastGhost || time - lastGhost.time > 0.05) {
            this._dodgeGhosts.push({ x: screenPos.x, y: baseY + walkBob + verticalOffset + feetOffset, time, facing: this.facing });
            if (this._dodgeGhosts.length > 5) this._dodgeGhosts.shift();
          }
          // Draw ghost sprites
          for (let i = 0; i < this._dodgeGhosts.length; i++) {
            const ghost = this._dodgeGhosts[i];
            const age = time - ghost.time;
            if (age > 0.3) continue;
            ctx.globalAlpha = (1 - age / 0.3) * 0.3;
            this._lpcRenderer.drawComposite(
              ctx, this._lpcLayers, 'walk', ghost.facing, frameIndex,
              ghost.x, ghost.y, spriteW, spriteH
            );
          }
          ctx.globalAlpha = 0.7;
        } else if (this._dodgeGhosts.length > 0) {
          this._dodgeGhosts.length = 0;
        }

        const drawn = this._lpcRenderer.drawComposite(
          ctx,
          this._lpcLayers,
          lpcAnimState,
          this.facing,
          frameIndex,
          screenPos.x,
          baseY + walkBob + verticalOffset + breathOffset + feetOffset,
          spriteW,
          spriteH
        );

        // Equipment glow effect (rare+ items)
        if (drawn) {
          const weapon = this.inventory.getEquippedItem('weapon');
          const chest = this.inventory.getEquippedItem('chest');
          const glowColor = weapon?.visual?.glow || chest?.visual?.glow;
          if (glowColor) {
            const pulse = 0.4 + Math.sin(time * 3) * 0.15;
            ctx.globalAlpha = pulse;
            ctx.shadowColor = glowColor;
            ctx.shadowBlur = 15 * z;
            // Re-draw sprite with glow (shadow acts as glow)
            this._lpcRenderer.drawComposite(
              ctx, this._lpcLayers, lpcAnimState, this.facing, frameIndex,
              screenPos.x, baseY + walkBob + verticalOffset + breathOffset + feetOffset, spriteW, spriteH
            );
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;
          }
        }

        // Hit flash overlay for LPC sprites
        if (drawn && isHit && this.hitFlashDuration > 0) {
          const flashProgress = 1 - (this.hitFlashTimer / this.hitFlashDuration);
          const alpha = Math.max(0, 1 - flashProgress) * 0.6;
          ctx.globalCompositeOperation = 'source-atop';
          ctx.globalAlpha = alpha;
          ctx.fillStyle = flashProgress < 0.5 ? '#ffffff' : '#ff4444';
          ctx.fillRect(screenPos.x - spriteW / 2, baseY + walkBob + verticalOffset + breathOffset + feetOffset - spriteH, spriteW, spriteH);
          ctx.globalCompositeOperation = 'source-over';
        }

        // Status effect visual overlays on player sprite
        if (drawn) {
          this.renderPlayerStatusOverlays(ctx, screenPos.x, baseY + walkBob + verticalOffset + breathOffset + feetOffset, spriteW, spriteH, z, time);
        }

        ctx.restore();
        ctx.globalAlpha = 1;
        return; // Skip procedural rendering
      }
    }

    // === PROCEDURAL FALLBACK (when LPC sprites are not loaded) ===

    // --- Frame cache: discretize animation and cache rendered body ---
    const animState = this.isAttacking ? 'atk' : isMoving ? 'walk' : 'idle';
    let animPhase: number;
    if (animState === 'idle') {
      animPhase = Math.floor(time * 4) % 8;
    } else if (animState === 'walk') {
      animPhase = Math.floor(walkCycle * 2.5) % 16;
    } else {
      animPhase = Math.min(3, Math.floor(time * 12) % 4);
    }

    // Invalidate cache on zoom/equipment change
    const equipHash = this.getEquipVisualHash();
    if (z !== this._frameCacheZoom || equipHash !== this._frameCacheEquipHash) {
      this._frameCache.clear();
      this._frameCacheZoom = z;
      this._frameCacheEquipHash = equipHash;
    }

    const cacheKey = `${this.facing}_${animState}_${animPhase}`;
    const useCache = !isHit && !this.isDodging;
    let cachedBody = useCache ? this._frameCache.get(cacheKey) : undefined;

    // Offscreen canvas dimensions
    const cw = Math.ceil(Player.FRAME_CACHE_W * z);
    const ch = Math.ceil(Player.FRAME_CACHE_H * z);
    const cx = cw / 2;      // origin X in offscreen canvas
    const cy = ch * 0.75;   // origin Y (feet position)

    if (!cachedBody) {
      // Cache miss: render body to offscreen canvas
      if (!this._frameCacheCanvas || this._frameCacheCanvas.width !== cw || this._frameCacheCanvas.height !== ch) {
        this._frameCacheCanvas = document.createElement('canvas');
        this._frameCacheCanvas.width = cw;
        this._frameCacheCanvas.height = ch;
        this._frameCacheCtx = this._frameCacheCanvas.getContext('2d')!;
      }
      const oc = this._frameCacheCtx!;
      oc.clearRect(0, 0, cw, ch);
      oc.save();
      oc.translate(cx, cy);

      // Render body procedurally to offscreen canvas
      this.renderBodyProcedural(oc, z, time, isMoving, walkCycle, breathe, facingMult, isHit);

      oc.restore();

      if (useCache) {
        // Copy to a dedicated cache canvas
        const cacheCanvas = document.createElement('canvas');
        cacheCanvas.width = cw;
        cacheCanvas.height = ch;
        cacheCanvas.getContext('2d')!.drawImage(this._frameCacheCanvas!, 0, 0);
        this._frameCache.set(cacheKey, cacheCanvas);
        cachedBody = cacheCanvas;
      } else {
        cachedBody = this._frameCacheCanvas!;
      }
    }

    // Draw cached/rendered body to main canvas
    ctx.save();
    if (this.isDodging) ctx.globalAlpha = 0.7;
    const destX = screenPos.x - cx;
    const destY = baseY + walkBob + verticalOffset - cy;
    ctx.drawImage(cachedBody, destX, destY);

    // === HIT FLASH OVERLAY ===
    if (isHit && this.hitFlashDuration > 0) {
      const flashProgress = 1 - (this.hitFlashTimer / this.hitFlashDuration);
      const whiteAmount = Math.max(0, 1 - flashProgress * 2);
      const redAmount = Math.min(1, flashProgress);
      const alpha = Math.max(0, 1 - flashProgress) * 0.6;
      const r = 255;
      const g = Math.floor(255 * whiteAmount);
      const b = Math.floor(255 * whiteAmount * (1 - redAmount));

      // Draw hit flash onto the main canvas using the body area
      ctx.globalCompositeOperation = 'source-atop';
      ctx.globalAlpha = alpha;
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(destX, destY, cw, ch);
      ctx.globalCompositeOperation = 'source-over';
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** Render the full character body procedurally onto a context translated to character origin */
  private renderBodyProcedural(
    ctx: CanvasRenderingContext2D,
    z: number,
    time: number,
    isMoving: boolean,
    walkCycle: number,
    breathe: number,
    facingMult: number,
    isHit: boolean
  ): void {
    // Get equipped items
    const equippedWeapon = this.inventory.getEquippedItem('weapon');
    const equippedChest = this.inventory.getEquippedItem('chest');
    const equippedBoots = this.inventory.getEquippedItem('boots');
    const equippedGloves = this.inventory.getEquippedItem('gloves');
    const equippedCape = this.inventory.getEquippedItem('cape');
    const equippedHead = this.inventory.getEquippedItem('head');
    const equippedBelt = this.inventory.getEquippedItem('belt');

    // Use static class palettes
    const palette = CLASS_PALETTES[this.className] || CLASS_PALETTES.alp;

    // Override colors from equipment
    const armorColor = equippedChest?.visual?.primaryColor || palette.armor;
    const armorLight = equippedChest?.visual?.secondaryColor || palette.armorLight;
    const armorDark = this.darkenColor(armorColor, 0.3);
    const accentColor = equippedChest?.visual?.accentColor || palette.accent;
    const bootColor = equippedBoots?.visual?.primaryColor || palette.armorDark;
    const gloveColor = equippedGloves?.visual?.primaryColor || palette.armor;
    const beltColor = equippedBelt?.visual?.primaryColor || palette.armorDark;

    let skinColor = palette.skin;
    let skinLight = palette.skinLight;
    if (isHit) {
      skinColor = '#FF6B6B';
      skinLight = '#FF8888';
    }

    // === CAPE ===
    if (equippedCape) {
      this.renderCape(ctx, z, time, isMoving, equippedCape, facingMult);
    }

    // Leg animation
    const legAngle = isMoving ? Math.sin(walkCycle) * 0.4 : 0;

    // === BACK LEG ===
    ctx.save();
    ctx.translate(-4 * z * facingMult, -16 * z);
    ctx.rotate(-legAngle * facingMult);
    this.renderLeg(ctx, z, bootColor, palette.cloth, -1);
    ctx.restore();

    // === BACK ARM ===
    const armSwing = isMoving ? Math.sin(walkCycle) * 0.5 : 0;
    ctx.save();
    ctx.translate(-8 * z * facingMult, -38 * z + breathe * z);
    ctx.rotate(-armSwing * facingMult * 0.8);
    this.renderArm(ctx, z, armorColor, armorLight, gloveColor, skinColor, skinLight, -1, facingMult);
    ctx.restore();

    // === BODY / TORSO ===
    this.renderTorso(ctx, z, armorColor, armorLight, armorDark, accentColor, beltColor, palette.cloth, breathe, equippedChest, isHit);

    // === FRONT LEG ===
    ctx.save();
    ctx.translate(4 * z * facingMult, -16 * z);
    ctx.rotate(legAngle * facingMult);
    this.renderLeg(ctx, z, bootColor, palette.cloth, 1);
    ctx.restore();

    // === HEAD ===
    this.renderHead(ctx, z, skinColor, skinLight, palette.skinShadow, palette.hair, palette.hairLight,
                    facingMult, breathe, equippedHead, palette, isHit);

    // === FRONT ARM (with weapon) ===
    ctx.save();
    ctx.translate(8 * z * facingMult, -38 * z + breathe * z);
    let attackAngle = 0;
    if (this.isAttacking) {
      attackAngle = facingMult > 0 ? 0.8 : -0.8;
    } else {
      attackAngle = armSwing * facingMult * 0.8;
    }
    ctx.rotate(attackAngle);
    this.renderArm(ctx, z, armorColor, armorLight, gloveColor, skinColor, skinLight, 1, facingMult);
    this.renderWeapon(ctx, z, 18 * z, facingMult, equippedWeapon, accentColor);
    ctx.restore();

    // === GLOW EFFECTS ===
    if (equippedChest?.visual?.glow && !isHit) {
      ctx.shadowColor = equippedChest.visual.glow;
      ctx.shadowBlur = 15 * z;
      ctx.strokeStyle = equippedChest.visual.glow + '40';
      ctx.lineWidth = 2 * z;
      ctx.beginPath();
      ctx.ellipse(0, -32 * z, 14 * z, 18 * z, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  private renderCape(ctx: CanvasRenderingContext2D, z: number, time: number, isMoving: boolean,
                     cape: import('@shared/types').EquipmentItem, _facingMult: number): void {
    const capeColor = cape.visual?.primaryColor || '#8B0000';
    const capeInner = cape.visual?.secondaryColor || this.darkenColor(capeColor, 0.4);

    const wave1 = Math.sin(time * 2.5) * 3;
    const wave2 = Math.sin(time * 3.2 + 1) * 2;
    const moveWave = isMoving ? Math.sin(time * 8) * 4 : 0;

    // Cape outer
    ctx.beginPath();
    ctx.moveTo(-8 * z, -48 * z);
    ctx.quadraticCurveTo(-6 * z, -44 * z, -10 * z, -35 * z);
    ctx.bezierCurveTo(-14 * z + wave1 * z, -20 * z, -16 * z + wave2 * z + moveWave * z, 0, -10 * z + moveWave * 0.5 * z, 8 * z);
    ctx.lineTo(10 * z - moveWave * 0.5 * z, 8 * z);
    ctx.bezierCurveTo(16 * z - wave2 * z - moveWave * z, 0, 14 * z - wave1 * z, -20 * z, 10 * z, -35 * z);
    ctx.quadraticCurveTo(6 * z, -44 * z, 8 * z, -48 * z);
    ctx.closePath();

    const capeGrad = ctx.createLinearGradient(0, -48 * z, 0, 8 * z);
    capeGrad.addColorStop(0, capeColor);
    capeGrad.addColorStop(0.5, this.darkenColor(capeColor, 0.15));
    capeGrad.addColorStop(1, this.darkenColor(capeColor, 0.3));
    ctx.fillStyle = capeGrad;
    ctx.fill();

    // Cape inner lining
    ctx.beginPath();
    ctx.moveTo(-6 * z, -46 * z);
    ctx.bezierCurveTo(-10 * z + wave1 * 0.7 * z, -20 * z, -12 * z + wave2 * 0.7 * z + moveWave * 0.7 * z, -5 * z, -8 * z + moveWave * 0.3 * z, 4 * z);
    ctx.lineTo(8 * z - moveWave * 0.3 * z, 4 * z);
    ctx.bezierCurveTo(12 * z - wave2 * 0.7 * z - moveWave * 0.7 * z, -5 * z, 10 * z - wave1 * 0.7 * z, -20 * z, 6 * z, -46 * z);
    ctx.closePath();
    ctx.fillStyle = capeInner;
    ctx.fill();

    // Cape clasp
    ctx.fillStyle = cape.visual?.accentColor || '#DAA520';
    ctx.beginPath();
    ctx.arc(0, -46 * z, 3 * z, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(-0.5 * z, -47 * z, 1 * z, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderLeg(ctx: CanvasRenderingContext2D, z: number, bootColor: string,
                    clothColor: string, _depth: number): void {
    // Upper leg (pants)
    const legGrad = ctx.createLinearGradient(-4 * z, 0, 4 * z, 0);
    legGrad.addColorStop(0, this.darkenColor(clothColor, 0.2));
    legGrad.addColorStop(0.5, clothColor);
    legGrad.addColorStop(1, this.darkenColor(clothColor, 0.1));

    ctx.fillStyle = legGrad;
    ctx.beginPath();
    ctx.moveTo(-4 * z, 0);
    ctx.lineTo(-3.5 * z, 12 * z);
    ctx.lineTo(3.5 * z, 12 * z);
    ctx.lineTo(4 * z, 0);
    ctx.closePath();
    ctx.fill();

    // Boot
    const bootGrad = ctx.createLinearGradient(-4 * z, 12 * z, 4 * z, 12 * z);
    bootGrad.addColorStop(0, this.darkenColor(bootColor, 0.2));
    bootGrad.addColorStop(0.4, bootColor);
    bootGrad.addColorStop(1, this.darkenColor(bootColor, 0.1));

    ctx.fillStyle = bootGrad;
    ctx.beginPath();
    ctx.moveTo(-4 * z, 11 * z);
    ctx.lineTo(-4.5 * z, 16 * z);
    ctx.lineTo(4.5 * z, 16 * z);
    ctx.lineTo(4 * z, 11 * z);
    ctx.closePath();
    ctx.fill();

    // Boot top trim
    ctx.fillStyle = this.lightenColor(bootColor, 0.2);
    ctx.fillRect(-4 * z, 11 * z, 8 * z, 1.5 * z);

    // Boot sole
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-4.5 * z, 15.5 * z, 9 * z, 1.5 * z);
  }

  private renderArm(ctx: CanvasRenderingContext2D, z: number, armorColor: string,
                    armorLight: string, gloveColor: string, skinColor: string,
                    skinLight: string, depth: number, _facingMult: number): void {
    // Shoulder pad
    ctx.fillStyle = armorColor;
    ctx.beginPath();
    ctx.ellipse(0, 2 * z, 5 * z, 4 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Shoulder highlight
    ctx.fillStyle = armorLight + '60';
    ctx.beginPath();
    ctx.ellipse(-1 * z, 1 * z, 2.5 * z, 2 * z, -0.3, 0, Math.PI * 2);
    ctx.fill();

    // Upper arm
    const armGrad = ctx.createLinearGradient(-3 * z, 4 * z, 3 * z, 4 * z);
    armGrad.addColorStop(0, this.darkenColor(armorColor, 0.15));
    armGrad.addColorStop(0.5, armorColor);
    armGrad.addColorStop(1, this.darkenColor(armorColor, 0.1));

    ctx.fillStyle = armGrad;
    ctx.beginPath();
    ctx.moveTo(-3 * z, 4 * z);
    ctx.lineTo(-2.5 * z, 12 * z);
    ctx.lineTo(2.5 * z, 12 * z);
    ctx.lineTo(3 * z, 4 * z);
    ctx.closePath();
    ctx.fill();

    // Forearm / Glove
    const gloveGrad = ctx.createLinearGradient(-2.5 * z, 12 * z, 2.5 * z, 12 * z);
    gloveGrad.addColorStop(0, this.darkenColor(gloveColor, 0.15));
    gloveGrad.addColorStop(0.5, gloveColor);
    gloveGrad.addColorStop(1, this.darkenColor(gloveColor, 0.1));

    ctx.fillStyle = gloveGrad;
    ctx.beginPath();
    ctx.moveTo(-2.5 * z, 11 * z);
    ctx.lineTo(-2.5 * z, 18 * z);
    ctx.lineTo(2.5 * z, 18 * z);
    ctx.lineTo(2.5 * z, 11 * z);
    ctx.closePath();
    ctx.fill();

    // Hand
    if (depth > 0) {
      ctx.fillStyle = skinColor;
      ctx.beginPath();
      ctx.ellipse(0, 20 * z, 3 * z, 2.5 * z, 0, 0, Math.PI * 2);
      ctx.fill();
      // Hand highlight
      ctx.fillStyle = skinLight + '80';
      ctx.beginPath();
      ctx.ellipse(-0.5 * z, 19 * z, 1.5 * z, 1 * z, -0.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderTorso(ctx: CanvasRenderingContext2D, z: number, armorColor: string,
                      armorLight: string, armorDark: string, accentColor: string,
                      beltColor: string, clothColor: string, breathe: number,
                      chest: import('@shared/types').EquipmentItem | null, isHit: boolean): void {
    const torsoY = -44 * z + breathe * z;

    // Body base (under armor)
    ctx.fillStyle = clothColor;
    ctx.beginPath();
    ctx.moveTo(-10 * z, torsoY);
    ctx.quadraticCurveTo(-12 * z, torsoY + 12 * z, -10 * z, torsoY + 28 * z);
    ctx.lineTo(10 * z, torsoY + 28 * z);
    ctx.quadraticCurveTo(12 * z, torsoY + 12 * z, 10 * z, torsoY);
    ctx.closePath();
    ctx.fill();

    // Main armor
    const armorGrad = ctx.createLinearGradient(-10 * z, torsoY, 10 * z, torsoY);
    armorGrad.addColorStop(0, armorDark);
    armorGrad.addColorStop(0.3, armorColor);
    armorGrad.addColorStop(0.6, armorLight);
    armorGrad.addColorStop(1, armorColor);

    ctx.fillStyle = armorGrad;
    ctx.beginPath();
    ctx.moveTo(-9 * z, torsoY + 2 * z);
    ctx.quadraticCurveTo(-11 * z, torsoY + 14 * z, -9 * z, torsoY + 22 * z);
    ctx.lineTo(9 * z, torsoY + 22 * z);
    ctx.quadraticCurveTo(11 * z, torsoY + 14 * z, 9 * z, torsoY + 2 * z);
    ctx.closePath();
    ctx.fill();

    // Chest plate center
    ctx.fillStyle = armorLight + '40';
    ctx.beginPath();
    ctx.moveTo(0, torsoY + 4 * z);
    ctx.lineTo(-5 * z, torsoY + 10 * z);
    ctx.lineTo(0, torsoY + 18 * z);
    ctx.lineTo(5 * z, torsoY + 10 * z);
    ctx.closePath();
    ctx.fill();

    // Armor style details
    const style = chest?.visual?.style || 'leather';
    if (style === 'plate' || style === 'legendary') {
      // Metal rivets
      ctx.fillStyle = accentColor;
      for (let i = 0; i < 2; i++) {
        ctx.beginPath();
        ctx.arc(-6 * z + i * 12 * z, torsoY + 8 * z, 1.5 * z, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(-6.5 * z + i * 12 * z, torsoY + 7.5 * z, 0.5 * z, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = accentColor;
      }

      // Horizontal plate lines
      ctx.strokeStyle = armorDark;
      ctx.lineWidth = 0.5 * z;
      ctx.beginPath();
      ctx.moveTo(-8 * z, torsoY + 14 * z);
      ctx.lineTo(8 * z, torsoY + 14 * z);
      ctx.stroke();
    } else if (style === 'chain') {
      // Chain mail texture
      ctx.strokeStyle = armorLight + '50';
      ctx.lineWidth = 0.3 * z;
      for (let y = 0; y < 4; y++) {
        for (let x = -2; x < 3; x++) {
          ctx.beginPath();
          ctx.arc(x * 3 * z + (y % 2) * 1.5 * z, torsoY + 6 * z + y * 4 * z, 1.5 * z, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }

    // Belt
    const beltGrad = ctx.createLinearGradient(-10 * z, torsoY + 22 * z, 10 * z, torsoY + 22 * z);
    beltGrad.addColorStop(0, this.darkenColor(beltColor, 0.2));
    beltGrad.addColorStop(0.5, beltColor);
    beltGrad.addColorStop(1, this.darkenColor(beltColor, 0.1));

    ctx.fillStyle = beltGrad;
    ctx.fillRect(-10 * z, torsoY + 22 * z, 20 * z, 5 * z);

    // Belt buckle
    ctx.fillStyle = accentColor;
    this.drawRoundedRect(ctx, -3.5 * z, torsoY + 22.5 * z, 7 * z, 4 * z, 1 * z);
    ctx.fillStyle = '#fff';
    ctx.fillRect(-1.5 * z, torsoY + 24 * z, 3 * z, 1.5 * z);

    // Hit effect
    if (isHit) {
      ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
      ctx.beginPath();
      ctx.ellipse(0, torsoY + 14 * z, 12 * z, 16 * z, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderHead(ctx: CanvasRenderingContext2D, z: number, skinColor: string,
                     skinLight: string, skinShadow: string, hairColor: string, hairLight: string,
                     facingMult: number, breathe: number,
                     head: import('@shared/types').EquipmentItem | null,
                     palette: { accent: string; accentLight: string; armor: string },
                     _isHit: boolean): void {
    const headY = -54 * z + breathe * z;

    // Neck
    ctx.fillStyle = skinColor;
    ctx.fillRect(-3 * z, headY + 8 * z, 6 * z, 6 * z);
    ctx.fillStyle = skinShadow;
    ctx.fillRect(-3 * z, headY + 8 * z, 2 * z, 6 * z);

    // Head shape
    const headGrad = ctx.createRadialGradient(2 * z * facingMult, headY - 2 * z, 0, 0, headY, 10 * z);
    headGrad.addColorStop(0, skinLight);
    headGrad.addColorStop(0.7, skinColor);
    headGrad.addColorStop(1, skinShadow);

    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.ellipse(0, headY, 9 * z, 10 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Determine headgear
    const hasHelmet = head || this.className === 'alp';
    const helmetColor = head?.visual?.primaryColor || palette.armor;
    const helmetAccent = head?.visual?.accentColor || palette.accent;

    if (hasHelmet) {
      this.renderHelmet(ctx, z, headY, helmetColor, helmetAccent, head, facingMult);
    } else {
      // Hair
      this.renderHair(ctx, z, headY, hairColor, hairLight, facingMult);
    }

    // Face (if visible)
    const helmetStyle = head?.visual?.style;
    if (!hasHelmet || helmetStyle !== 'plate') {
      this.renderFace(ctx, z, headY, skinColor, facingMult);
    }

    // Head equipment glow
    if (head?.visual?.glow) {
      ctx.shadowColor = head.visual.glow;
      ctx.shadowBlur = 10 * z;
      ctx.strokeStyle = head.visual.glow + '60';
      ctx.lineWidth = 1.5 * z;
      ctx.beginPath();
      ctx.ellipse(0, headY - 2 * z, 11 * z, 10 * z, 0, 0, Math.PI);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  private renderHelmet(ctx: CanvasRenderingContext2D, z: number, headY: number,
                       color: string, accent: string,
                       head: import('@shared/types').EquipmentItem | null,
                       facingMult: number): void {
    const style = head?.visual?.style || 'plate';

    // Helmet base
    const helmGrad = ctx.createLinearGradient(-10 * z, headY - 10 * z, 10 * z, headY);
    helmGrad.addColorStop(0, this.darkenColor(color, 0.1));
    helmGrad.addColorStop(0.3, this.lightenColor(color, 0.15));
    helmGrad.addColorStop(0.7, color);
    helmGrad.addColorStop(1, this.darkenColor(color, 0.2));

    ctx.fillStyle = helmGrad;
    ctx.beginPath();
    ctx.arc(0, headY - 2 * z, 10 * z, Math.PI, 0);
    ctx.fill();

    // Helmet dome
    ctx.beginPath();
    ctx.ellipse(0, headY - 8 * z, 8 * z, 5 * z, 0, Math.PI, 0);
    ctx.fill();

    // Helmet shine
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.beginPath();
    ctx.ellipse(-3 * z, headY - 10 * z, 3 * z, 2 * z, -0.3, 0, Math.PI * 2);
    ctx.fill();

    if (style === 'plate' || style === 'legendary') {
      // Face guard
      ctx.fillStyle = this.darkenColor(color, 0.3);
      ctx.fillRect(-7 * z, headY - 4 * z, 14 * z, 8 * z);

      // Eye slits
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(-5 * z, headY - 2 * z, 4 * z, 2 * z);
      ctx.fillRect(1 * z, headY - 2 * z, 4 * z, 2 * z);

      // Nose guard
      ctx.fillStyle = color;
      ctx.fillRect(-1 * z, headY - 3 * z, 2 * z, 6 * z);
    }

    // Crest/Plume
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.moveTo(-1.5 * z, headY - 12 * z);
    ctx.quadraticCurveTo(0, headY - 20 * z, 3 * z * facingMult, headY - 18 * z);
    ctx.quadraticCurveTo(5 * z * facingMult, headY - 14 * z, 1.5 * z, headY - 12 * z);
    ctx.closePath();
    ctx.fill();

    // Plume detail
    ctx.strokeStyle = this.lightenColor(accent, 0.3);
    ctx.lineWidth = 0.5 * z;
    ctx.beginPath();
    ctx.moveTo(0, headY - 13 * z);
    ctx.quadraticCurveTo(1 * z * facingMult, headY - 17 * z, 2 * z * facingMult, headY - 16 * z);
    ctx.stroke();
  }

  private renderHair(ctx: CanvasRenderingContext2D, z: number, headY: number,
                     hairColor: string, hairLight: string, facingMult: number): void {
    // Hair base
    const hairGrad = ctx.createRadialGradient(2 * z * facingMult, headY - 6 * z, 0, 0, headY - 4 * z, 12 * z);
    hairGrad.addColorStop(0, hairLight);
    hairGrad.addColorStop(1, hairColor);

    ctx.fillStyle = hairGrad;
    ctx.beginPath();
    ctx.ellipse(0, headY - 4 * z, 10 * z, 8 * z, 0, Math.PI, 0);
    ctx.fill();

    // Hair strands
    ctx.strokeStyle = hairLight + '60';
    ctx.lineWidth = 0.5 * z;
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 2.5 * z, headY - 10 * z);
      ctx.quadraticCurveTo(i * 3 * z, headY - 14 * z, i * 2 * z + facingMult * z, headY - 12 * z);
      ctx.stroke();
    }

    // For Kam - mystical hood
    if (this.className === 'kam') {
      ctx.fillStyle = '#2E1A47';
      ctx.beginPath();
      ctx.moveTo(-11 * z, headY + 2 * z);
      ctx.quadraticCurveTo(-10 * z, headY - 8 * z, 0, headY - 16 * z);
      ctx.quadraticCurveTo(10 * z, headY - 8 * z, 11 * z, headY + 2 * z);
      ctx.quadraticCurveTo(6 * z, headY - 2 * z, 0, headY);
      ctx.quadraticCurveTo(-6 * z, headY - 2 * z, -11 * z, headY + 2 * z);
      ctx.fill();

      // Hood trim
      ctx.strokeStyle = '#00CED1';
      ctx.lineWidth = 1 * z;
      ctx.beginPath();
      ctx.moveTo(-10 * z, headY + 1 * z);
      ctx.quadraticCurveTo(0, headY - 1 * z, 10 * z, headY + 1 * z);
      ctx.stroke();
    }

    // For Mergen - headband
    if (this.className === 'mergen') {
      ctx.fillStyle = '#6B4423';
      ctx.fillRect(-10 * z, headY - 6 * z, 20 * z, 3 * z);

      // Feather
      ctx.fillStyle = '#FF8C00';
      ctx.beginPath();
      ctx.moveTo(6 * z, headY - 6 * z);
      ctx.quadraticCurveTo(12 * z, headY - 14 * z, 8 * z, headY - 20 * z);
      ctx.quadraticCurveTo(6 * z, headY - 14 * z, 5 * z, headY - 6 * z);
      ctx.fill();

      // Feather spine
      ctx.strokeStyle = '#5C4033';
      ctx.lineWidth = 0.5 * z;
      ctx.beginPath();
      ctx.moveTo(6 * z, headY - 7 * z);
      ctx.quadraticCurveTo(9 * z, headY - 14 * z, 8 * z, headY - 18 * z);
      ctx.stroke();
    }
  }

  private renderFace(ctx: CanvasRenderingContext2D, z: number, headY: number,
                     skinColor: string, facingMult: number): void {
    // Eye whites
    const eyeX = 3 * z * facingMult;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.ellipse(eyeX, headY - 1 * z, 2.5 * z, 2 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Iris
    ctx.fillStyle = '#4A3020';
    ctx.beginPath();
    ctx.arc(eyeX + 0.5 * z * facingMult, headY - 0.5 * z, 1.5 * z, 0, Math.PI * 2);
    ctx.fill();

    // Pupil
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(eyeX + 0.7 * z * facingMult, headY - 0.3 * z, 0.8 * z, 0, Math.PI * 2);
    ctx.fill();

    // Eye highlight
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(eyeX + 0.3 * z * facingMult, headY - 1 * z, 0.5 * z, 0, Math.PI * 2);
    ctx.fill();

    // Eyebrow
    ctx.strokeStyle = '#3d2817';
    ctx.lineWidth = 1.2 * z;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(eyeX - 2.5 * z * facingMult, headY - 4 * z);
    ctx.quadraticCurveTo(eyeX, headY - 5 * z, eyeX + 2 * z * facingMult, headY - 4 * z);
    ctx.stroke();

    // Nose hint
    ctx.strokeStyle = this.darkenColor(skinColor, 0.15);
    ctx.lineWidth = 0.8 * z;
    ctx.beginPath();
    ctx.moveTo(1 * z * facingMult, headY);
    ctx.lineTo(2 * z * facingMult, headY + 3 * z);
    ctx.stroke();

    // Mouth
    ctx.strokeStyle = this.darkenColor(skinColor, 0.25);
    ctx.lineWidth = 0.8 * z;
    ctx.beginPath();
    ctx.moveTo(-2 * z, headY + 5 * z);
    ctx.quadraticCurveTo(0, headY + 5.5 * z, 2 * z, headY + 5 * z);
    ctx.stroke();
  }

  private static _colorCache = new Map<string, string>();

  private darkenColor(hex: string, factor: number): string {
    const key = `d_${hex}_${factor}`;
    let cached = Player._colorCache.get(key);
    if (cached !== undefined) return cached;
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, Math.floor((num >> 16) * (1 - factor)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) * (1 - factor)));
    const b = Math.max(0, Math.floor((num & 0x0000FF) * (1 - factor)));
    cached = `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
    Player._colorCache.set(key, cached);
    return cached;
  }

  private lightenColor(hex: string, factor: number): string {
    const key = `l_${hex}_${factor}`;
    let cached = Player._colorCache.get(key);
    if (cached !== undefined) return cached;
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * factor));
    const g = Math.min(255, Math.floor(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * factor));
    const b = Math.min(255, Math.floor((num & 0x0000FF) + (255 - (num & 0x0000FF)) * factor));
    cached = `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
    Player._colorCache.set(key, cached);
    return cached;
  }

  private renderWeapon(
    ctx: CanvasRenderingContext2D,
    z: number,
    armLength: number,
    facingMult: number,
    weapon: import('@shared/types').EquipmentItem | null,
    defaultAccent: string
  ): void {
    const weaponColor = weapon?.visual?.primaryColor || '#c0c0c0';
    const weaponAccent = weapon?.visual?.accentColor || defaultAccent;
    const weaponHandle = weapon?.visual?.secondaryColor || '#5c3d2e';

    // Weapon glow
    if (weapon?.visual?.glow) {
      ctx.shadowColor = weapon.visual.glow;
      ctx.shadowBlur = 12 * z;
    }

    if (this.className === 'alp') {
      // === KILIÇ (SWORD) ===
      const bladeLength = 32 * z;
      const bladeWidth = 4 * z;
      const handleY = armLength;

      // Handle wrap
      ctx.fillStyle = weaponHandle;
      ctx.beginPath();
      ctx.roundRect(-2 * z, handleY - 2 * z, 4 * z, 10 * z, 1 * z);
      ctx.fill();

      // Handle wrap pattern
      ctx.strokeStyle = this.darkenColor(weaponHandle, 0.3);
      ctx.lineWidth = 0.5 * z;
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(-2 * z, handleY + i * 2.5 * z);
        ctx.lineTo(2 * z, handleY + 1 * z + i * 2.5 * z);
        ctx.stroke();
      }

      // Cross guard
      const guardGrad = ctx.createLinearGradient(-8 * z, handleY + 8 * z, 8 * z, handleY + 8 * z);
      guardGrad.addColorStop(0, this.darkenColor(weaponAccent, 0.2));
      guardGrad.addColorStop(0.5, this.lightenColor(weaponAccent, 0.2));
      guardGrad.addColorStop(1, this.darkenColor(weaponAccent, 0.1));
      ctx.fillStyle = guardGrad;
      ctx.beginPath();
      ctx.roundRect(-8 * z, handleY + 7 * z, 16 * z, 4 * z, 1 * z);
      ctx.fill();

      // Guard gems
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.arc(-5 * z, handleY + 9 * z, 1.2 * z, 0, Math.PI * 2);
      ctx.arc(5 * z, handleY + 9 * z, 1.2 * z, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(-5.3 * z, handleY + 8.7 * z, 0.4 * z, 0, Math.PI * 2);
      ctx.arc(4.7 * z, handleY + 8.7 * z, 0.4 * z, 0, Math.PI * 2);
      ctx.fill();

      // Blade
      const bladeGrad = ctx.createLinearGradient(-bladeWidth / 2, handleY + 10 * z, bladeWidth / 2, handleY + 10 * z);
      bladeGrad.addColorStop(0, this.darkenColor(weaponColor, 0.15));
      bladeGrad.addColorStop(0.3, '#ffffff');
      bladeGrad.addColorStop(0.5, weaponColor);
      bladeGrad.addColorStop(0.7, this.lightenColor(weaponColor, 0.3));
      bladeGrad.addColorStop(1, this.darkenColor(weaponColor, 0.1));

      ctx.fillStyle = bladeGrad;
      ctx.beginPath();
      ctx.moveTo(-bladeWidth / 2, handleY + 10 * z);
      ctx.lineTo(-bladeWidth / 2, handleY + 10 * z + bladeLength * 0.85);
      ctx.lineTo(0, handleY + 10 * z + bladeLength);
      ctx.lineTo(bladeWidth / 2, handleY + 10 * z + bladeLength * 0.85);
      ctx.lineTo(bladeWidth / 2, handleY + 10 * z);
      ctx.closePath();
      ctx.fill();

      // Blade edge highlight
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 0.8 * z;
      ctx.beginPath();
      ctx.moveTo(0, handleY + 12 * z);
      ctx.lineTo(0, handleY + 10 * z + bladeLength - 2 * z);
      ctx.stroke();

      // Fuller (blood groove)
      ctx.strokeStyle = this.darkenColor(weaponColor, 0.25);
      ctx.lineWidth = 1 * z;
      ctx.beginPath();
      ctx.moveTo(0, handleY + 14 * z);
      ctx.lineTo(0, handleY + 10 * z + bladeLength * 0.7);
      ctx.stroke();

      // Pommel
      const pommelGrad = ctx.createRadialGradient(0, handleY - 3 * z, 0, 0, handleY - 3 * z, 3 * z);
      pommelGrad.addColorStop(0, this.lightenColor(weaponAccent, 0.3));
      pommelGrad.addColorStop(1, weaponAccent);
      ctx.fillStyle = pommelGrad;
      ctx.beginPath();
      ctx.arc(0, handleY - 3 * z, 3 * z, 0, Math.PI * 2);
      ctx.fill();

    } else if (this.className === 'kam') {
      // === ASA (STAFF) ===
      const staffLength = 50 * z;
      const staffY = armLength - 25 * z;

      // Staff body with wood grain
      const staffGrad = ctx.createLinearGradient(-2.5 * z, staffY, 2.5 * z, staffY);
      staffGrad.addColorStop(0, this.darkenColor(weaponHandle, 0.2));
      staffGrad.addColorStop(0.3, weaponHandle);
      staffGrad.addColorStop(0.6, this.lightenColor(weaponHandle, 0.1));
      staffGrad.addColorStop(1, this.darkenColor(weaponHandle, 0.15));

      ctx.fillStyle = staffGrad;
      ctx.beginPath();
      ctx.roundRect(-2.5 * z, staffY, 5 * z, staffLength, 1.5 * z);
      ctx.fill();

      // Wood grain lines
      ctx.strokeStyle = this.darkenColor(weaponHandle, 0.25) + '60';
      ctx.lineWidth = 0.3 * z;
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.moveTo(-1.5 * z, staffY + 5 * z + i * 6 * z);
        ctx.quadraticCurveTo(0, staffY + 7 * z + i * 6 * z, 1.5 * z, staffY + 5 * z + i * 6 * z);
        ctx.stroke();
      }

      // Metal bands
      const bandGrad = ctx.createLinearGradient(-3.5 * z, 0, 3.5 * z, 0);
      bandGrad.addColorStop(0, this.darkenColor(weaponAccent, 0.2));
      bandGrad.addColorStop(0.5, this.lightenColor(weaponAccent, 0.3));
      bandGrad.addColorStop(1, weaponAccent);

      ctx.fillStyle = bandGrad;
      ctx.fillRect(-3.5 * z, staffY + 5 * z, 7 * z, 3 * z);
      ctx.fillRect(-3.5 * z, staffY + staffLength - 8 * z, 7 * z, 3 * z);

      // Orb holder (claw design)
      ctx.fillStyle = weaponAccent;
      for (let i = 0; i < 3; i++) {
        const angle = -Math.PI / 3 + i * Math.PI / 3;
        ctx.beginPath();
        ctx.moveTo(0, staffY - 2 * z);
        ctx.quadraticCurveTo(
          Math.cos(angle) * 4 * z, staffY - 8 * z,
          Math.cos(angle) * 6 * z, staffY - 12 * z
        );
        ctx.lineTo(Math.cos(angle) * 4 * z, staffY - 10 * z);
        ctx.quadraticCurveTo(Math.cos(angle) * 2 * z, staffY - 6 * z, 0, staffY);
        ctx.fill();
      }

      // Magical orb
      const orbY = staffY - 14 * z;
      const orbRadius = 7 * z;
      const time = performance.now() / 1000;

      // Orb outer glow
      const outerGlow = ctx.createRadialGradient(0, orbY, orbRadius * 0.5, 0, orbY, orbRadius * 2);
      outerGlow.addColorStop(0, weaponColor + '60');
      outerGlow.addColorStop(0.5, weaponColor + '30');
      outerGlow.addColorStop(1, weaponColor + '00');
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.arc(0, orbY, orbRadius * 2, 0, Math.PI * 2);
      ctx.fill();

      // Orb main
      const orbGrad = ctx.createRadialGradient(-2 * z, orbY - 2 * z, 0, 0, orbY, orbRadius);
      orbGrad.addColorStop(0, '#ffffff');
      orbGrad.addColorStop(0.2, this.lightenColor(weaponColor, 0.4));
      orbGrad.addColorStop(0.6, weaponColor);
      orbGrad.addColorStop(1, this.darkenColor(weaponAccent, 0.2));
      ctx.fillStyle = orbGrad;
      ctx.beginPath();
      ctx.arc(0, orbY, orbRadius, 0, Math.PI * 2);
      ctx.fill();

      // Swirling energy inside
      ctx.strokeStyle = '#ffffff80';
      ctx.lineWidth = 0.8 * z;
      ctx.beginPath();
      const swirl = time * 2;
      ctx.arc(Math.cos(swirl) * 2 * z, orbY + Math.sin(swirl) * 2 * z, 3 * z, 0, Math.PI);
      ctx.stroke();

      // Orb highlight
      ctx.fillStyle = '#ffffff90';
      ctx.beginPath();
      ctx.ellipse(-2 * z, orbY - 3 * z, 2 * z, 1.5 * z, -0.4, 0, Math.PI * 2);
      ctx.fill();

    } else if (this.className === 'mergen') {
      // === YAY (BOW) ===
      const bowRadius = 20 * z;
      const bowCenterX = 4 * z * facingMult;
      const bowY = armLength - 2 * z;

      // Bow limbs with gradient
      const limbGrad = ctx.createLinearGradient(bowCenterX - bowRadius, bowY, bowCenterX + bowRadius, bowY);
      limbGrad.addColorStop(0, this.darkenColor(weaponHandle, 0.2));
      limbGrad.addColorStop(0.5, this.lightenColor(weaponHandle, 0.1));
      limbGrad.addColorStop(1, this.darkenColor(weaponHandle, 0.1));

      ctx.strokeStyle = limbGrad;
      ctx.lineWidth = 4 * z;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(bowCenterX, bowY, bowRadius, -Math.PI * 0.5, Math.PI * 0.5);
      ctx.stroke();

      // Bow inner edge
      ctx.strokeStyle = this.darkenColor(weaponHandle, 0.3);
      ctx.lineWidth = 1.5 * z;
      ctx.beginPath();
      ctx.arc(bowCenterX - 2 * z * facingMult, bowY, bowRadius - 2 * z, -Math.PI * 0.45, Math.PI * 0.45);
      ctx.stroke();

      // Bow tips with decorations
      const tipAngles = [-Math.PI * 0.5, Math.PI * 0.5];
      for (const angle of tipAngles) {
        const tipX = bowCenterX + Math.cos(angle) * bowRadius;
        const tipY = bowY + Math.sin(angle) * bowRadius;

        // Tip cap
        const capGrad = ctx.createRadialGradient(tipX, tipY, 0, tipX, tipY, 4 * z);
        capGrad.addColorStop(0, this.lightenColor(weaponAccent, 0.3));
        capGrad.addColorStop(1, weaponAccent);
        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.arc(tipX, tipY, 3.5 * z, 0, Math.PI * 2);
        ctx.fill();

        // Tip gem
        ctx.fillStyle = '#88ff88';
        ctx.beginPath();
        ctx.arc(tipX, tipY, 1.5 * z, 0, Math.PI * 2);
        ctx.fill();
      }

      // Grip wrapping
      ctx.fillStyle = weaponAccent;
      ctx.beginPath();
      ctx.roundRect(bowCenterX - 3 * z, bowY - 5 * z, 5 * z, 10 * z, 2 * z);
      ctx.fill();

      // Grip pattern
      ctx.strokeStyle = this.darkenColor(weaponAccent, 0.3);
      ctx.lineWidth = 0.5 * z;
      for (let i = -2; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(bowCenterX - 3 * z, bowY + i * 2 * z);
        ctx.lineTo(bowCenterX + 2 * z, bowY + i * 2 * z + 1 * z);
        ctx.stroke();
      }

      // Bowstring
      const stringPull = this.isAttacking ? 8 * z * facingMult : 0;
      ctx.strokeStyle = '#e8e8e8';
      ctx.lineWidth = 1 * z;
      ctx.beginPath();
      ctx.moveTo(bowCenterX + Math.cos(-Math.PI * 0.5) * bowRadius, bowY + Math.sin(-Math.PI * 0.5) * bowRadius);
      ctx.quadraticCurveTo(bowCenterX - stringPull, bowY, bowCenterX + Math.cos(Math.PI * 0.5) * bowRadius, bowY + Math.sin(Math.PI * 0.5) * bowRadius);
      ctx.stroke();

      // Arrow (when attacking)
      if (this.isAttacking) {
        const arrowX = bowCenterX - stringPull + 2 * z * facingMult;

        // Arrow shaft
        ctx.fillStyle = weaponHandle;
        ctx.fillRect(arrowX - 0.8 * z, bowY - 18 * z, 1.6 * z, 36 * z);

        // Arrow fletching
        ctx.fillStyle = '#cc4444';
        ctx.beginPath();
        ctx.moveTo(arrowX, bowY + 14 * z);
        ctx.lineTo(arrowX - 3 * z, bowY + 20 * z);
        ctx.lineTo(arrowX, bowY + 18 * z);
        ctx.lineTo(arrowX + 3 * z, bowY + 20 * z);
        ctx.closePath();
        ctx.fill();

        // Arrowhead
        const headGrad = ctx.createLinearGradient(arrowX - 3 * z, bowY - 22 * z, arrowX + 3 * z, bowY - 18 * z);
        headGrad.addColorStop(0, '#888888');
        headGrad.addColorStop(0.5, '#ffffff');
        headGrad.addColorStop(1, '#aaaaaa');
        ctx.fillStyle = headGrad;
        ctx.beginPath();
        ctx.moveTo(arrowX, bowY - 26 * z);
        ctx.lineTo(arrowX - 3 * z, bowY - 18 * z);
        ctx.lineTo(arrowX + 3 * z, bowY - 18 * z);
        ctx.closePath();
        ctx.fill();
      }
    }

    ctx.shadowBlur = 0;
  }

  private drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  }

  // Experience and leveling
  addExperience(amount: number): boolean {
    if (this.level >= MAX_LEVEL) return false;

    this.experience += amount;
    let didLevelUp = false;

    while (this.level < MAX_LEVEL && this.experience >= this.getExperienceForNextLevel()) {
      const expNeeded = this.getExperienceForNextLevel();
      this.experience -= expNeeded;
      this.levelUp();
      didLevelUp = true;
    }

    // Cap experience at max level
    if (this.level >= MAX_LEVEL) {
      this.experience = 0;
    }

    return didLevelUp;
  }

  /** Flag: true when branch selection is pending (level 10 or 40) */
  public branchSelectionPending: boolean = false;

  private levelUp(): void {
    this.level++;
    this.skillPoints++;

    // Passive points: none before lv10, +10 at lv10 (burst), +1 per level after
    if (this.level === 10) {
      for (let i = 0; i < 10; i++) this.passiveTree.addPoint();
      this.passiveTree.addBranchPoint(); // 1 branch point to select first branch
    } else if (this.level > 10) {
      this.passiveTree.addPoint();
    }

    // Level 40: second branch point
    if (this.level === 40) {
      this.passiveTree.addBranchPoint(); // 1 branch point to select second branch
    }

    // Increase BASE stats (will be applied via recalculateStats)
    this.baseStats.maxHp += 10;
    this.baseStats.maxMana += 5;

    // Recalculate all stats (base + equipment + passives)
    this.recalculateStats();

    // Full heal on level up
    this.stats.hp = this.stats.maxHp;
    this.stats.mana = this.stats.maxMana;

    // Play level up sound
    getAudioManager().play('ui_level_up');

    // Level up visual effects - golden particle burst
    const ps = getParticleSystem();
    if (ps) {
      // Golden upward burst
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 30,
        color: ['#ffd700', '#ffaa00', '#ffffff', '#ffe066'],
        speed: { min: 60, max: 200 },
        size: { min: 2, max: 5 },
        life: { min: 0.5, max: 1.2 },
        direction: -Math.PI / 2,
        spread: Math.PI * 0.6,
        gravity: -40,
        friction: 1.5,
        alphaDecay: 0.8,
        sizeDecay: 2,
        shape: 'star',
      });
      // Ground ring burst
      ps.emit({
        position: { x: this.position.x, y: this.position.y },
        count: 16,
        color: ['#ffd700', '#ffcc33'],
        speed: { min: 100, max: 250 },
        size: { min: 2, max: 4 },
        life: { min: 0.3, max: 0.6 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 0,
        friction: 4,
        alphaDecay: 1.5,
        sizeDecay: 3,
        shape: 'circle',
      });
    }

    // Screen shake for impact
    const shake = getScreenShake();
    if (shake) {
      shake.shake({ intensity: 4, duration: 0.3 }); // Subtle celebratory shake
      shake.triggerFlash('#ffd700', 0.3, 0.25); // Golden flash
    }

    debugLog('Player', `Level up! Now level ${this.level}`);
  }

  /**
   * Get total passive bonuses
   */
  getPassiveBonuses(): PassiveBonuses {
    return this.passiveTree.getTotalBonuses();
  }

  /**
   * Check if Blood Magic keystone is active
   * When active, skills use HP instead of mana
   */
  hasBloodMagic(): boolean {
    return this.passiveTree.getTotalBonuses().bloodMagic === true;
  }

  /**
   * Check if Mind Over Matter keystone is active
   * When active, 30% of damage is taken from mana before HP
   */
  hasMindOverMatter(): boolean {
    return this.passiveTree.getTotalBonuses().mindOverMatter === true;
  }

  /**
   * Mark stat calculator as needing rebuild
   */
  invalidateStatCalculator(): void {
    this.statCalculatorDirty = true;
  }

  /**
   * Get the stat calculator, rebuilding if necessary
   */
  getStatCalculator(): StatCalculator {
    if (this.statCalculatorDirty) {
      this.rebuildStatCalculator();
    }
    return this.statCalculator;
  }

  /**
   * Rebuild the stat calculator from all sources
   */
  private rebuildStatCalculator(): void {
    this.statCalculator.clear();

    // 1. Add base stats as a source (from class/level, not current stats)
    this.statCalculator.addSource({
      id: 'base_stats',
      name: 'Base Stats',
      type: 'base',
      modifiers: [
        createModifier('strength', 'flat', this.baseStats.strength, 'base'),
        createModifier('agility', 'flat', this.baseStats.agility, 'base'),
        createModifier('spirit', 'flat', this.baseStats.spirit, 'base'),
        createModifier('maxHp', 'flat', this.baseStats.maxHp, 'base'),
        createModifier('maxMana', 'flat', this.baseStats.maxMana, 'base'),
        createModifier('armor', 'flat', this.baseStats.armor, 'base'),
        createModifier('evasion', 'flat', this.baseStats.evasion, 'base'),
      ],
    });

    // 2. Add equipment modifiers
    const equipmentSlots: EquipmentSlot[] = [
      'head', 'chest', 'gloves', 'belt', 'boots',
      'weapon', 'offhand', 'amulet', 'ring1', 'ring2', 'cape'
    ];

    for (const slot of equipmentSlots) {
      const item = this.inventory.getEquippedItem(slot);
      if (item) {
        const modifiers = extractEquipmentModifiers(item, `equipment_${slot}`);
        if (modifiers.length > 0) {
          this.statCalculator.addSource({
            id: `equipment_${slot}`,
            name: `${item.name} (${slot})`,
            type: 'item',
            modifiers,
          });
        }
      }
    }

    // 2b. Add set bonus modifiers from equipped set items
    const equippedSetIds: (string | undefined)[] = [];
    for (const slot of equipmentSlots) {
      const eqItem = this.inventory.getEquippedItem(slot);
      if (eqItem) {
        equippedSetIds.push(eqItem.setId);
      }
    }
    const setBonusModifiers = getSetBonusModifiersBySetIds(equippedSetIds);
    if (setBonusModifiers.length > 0) {
      this.statCalculator.addSource({
        id: 'set_bonuses',
        name: 'Set Bonuses',
        type: 'item',
        modifiers: setBonusModifiers,
      });
    }

    // 3. Add passive tree bonuses (legacy bonuses from existing nodes)
    const passiveBonuses = this.passiveTree.getTotalBonuses();
    const legacySource = createPassiveSource(passiveBonuses as Record<string, number>, 'passive_tree_legacy');
    this.statCalculator.addSource(legacySource);

    // 3b. Add new modifier path (mastery + jewel + direct modifiers)
    const extraModifiers = this.passiveTree.getAllModifiers();
    const extraTriggers = this.passiveTree.getAllTriggerEffects();
    if (extraModifiers.length > 0 || extraTriggers.length > 0) {
      this.statCalculator.addSource(
        createPassiveModifierSource(extraModifiers, extraTriggers, 'passive_tree_new')
      );
    }

    // 4. Add effect stone modifiers from skills
    this.addEffectStoneSources();

    // 5. Add active buff modifiers
    const buffModifiers = this.buffManager.getModifiers();
    if (buffModifiers.length > 0) {
      this.statCalculator.addSource({
        id: 'active_buffs',
        name: 'Active Buffs',
        type: 'buff',
        modifiers: buffModifiers,
      });
    }

    this.statCalculatorDirty = false;
  }

  /**
   * Add effect stone modifiers from all skills to stat calculator
   */
  private addEffectStoneSources(): void {
    for (const skill of this.customSkills) {
      for (let i = 0; i < skill.sockets.length; i++) {
        const stone = skill.sockets[i];
        if (!stone) continue;

        const sourceId = `skill_${skill.id}_stone_${i}`;
        const modifiers: ReturnType<typeof createModifier>[] = [];
        const triggerEffects: import('@shared/modifiers').TriggerEffect[] = [];

        // Convert effect stone properties to modifiers
        if (stone.effect.damageBonus) {
          modifiers.push(createModifier('damage', 'increased', stone.effect.damageBonus, sourceId));
        }
        if (stone.effect.areaBonus) {
          modifiers.push(createModifier('areaOfEffect', 'increased', stone.effect.areaBonus, sourceId));
        }

        // Lifesteal becomes a trigger effect
        if (stone.effect.lifesteal) {
          triggerEffects.push({
            id: `${sourceId}_lifesteal`,
            name: `${stone.name} Life Leech`,
            trigger: 'on_skill_hit',
            effect: { type: 'leech', stat: 'hp', percent: stone.effect.lifesteal },
            source: sourceId,
          });
        }

        // Add source if it has any modifiers or trigger effects
        if (modifiers.length > 0 || triggerEffects.length > 0) {
          this.statCalculator.addSource({
            id: sourceId,
            name: `${stone.name} (${skill.name})`,
            type: 'skill',
            modifiers,
            triggerEffects: triggerEffects.length > 0 ? triggerEffects : undefined,
          });
        }
      }
    }
  }

  /** Mark cached per-frame stats (e.g. moveSpeed) as stale so they are recalculated next frame. */
  markStatsDirty(): void { this._statsDirty = true; }

  /**
   * Recalculate and apply stats from all sources (base + equipment + passives)
   */
  recalculateStats(): void {
    this._statsDirty = true;
    this._lpcLayersDirty = true;
    this.rebuildStatCalculator();

    // Store current HP/Mana percentages to maintain ratio
    const hpPercent = this.stats.maxHp > 0 ? this.stats.hp / this.stats.maxHp : 1;
    const manaPercent = this.stats.maxMana > 0 ? this.stats.mana / this.stats.maxMana : 1;

    // Calculate attributes first (needed for attribute bonuses)
    this.stats.strength = Math.floor(this.statCalculator.calculate('strength'));
    this.stats.agility = Math.floor(this.statCalculator.calculate('agility'));
    this.stats.spirit = Math.floor(this.statCalculator.calculate('spirit'));

    // Apply attribute bonuses:
    // Güç: 1 puan = +2 HP, 10 puan = +%2 melee hasar
    // Çeviklik: 1 puan = +1 Evasion, 10 puan = +%2 saldırı hızı
    // Ruh: 1 puan = +2 Mana, 10 puan = +%2 ES
    const attrSourceId = 'attribute_bonuses';
    this.statCalculator.removeSource(attrSourceId);
    this.statCalculator.addSource({
      id: attrSourceId,
      name: 'Attribute Bonuses',
      type: 'base',
      modifiers: [
        // Strength bonuses
        createModifier('maxHp', 'flat', this.stats.strength * 2, attrSourceId),
        createModifier('physicalDamage', 'increased', Math.floor(this.stats.strength / 10) * 2, attrSourceId),
        // Agility bonuses
        createModifier('evasion', 'flat', this.stats.agility, attrSourceId),
        createModifier('attackSpeed', 'increased', Math.floor(this.stats.agility / 10) * 2, attrSourceId),
        // Spirit bonuses
        createModifier('maxMana', 'flat', this.stats.spirit * 2, attrSourceId),
        createModifier('energyShield', 'increased', Math.floor(this.stats.spirit / 10) * 2, attrSourceId),
      ],
    });

    // Now apply calculated stats (with attribute bonuses included)
    this.stats.maxHp = Math.floor(this.statCalculator.calculate('maxHp'));
    this.stats.maxMana = Math.floor(this.statCalculator.calculate('maxMana'));
    this.stats.maxEnergyShield = Math.floor(this.statCalculator.calculate('energyShield'));
    this.stats.energyShield = Math.min(this.stats.energyShield, this.stats.maxEnergyShield);
    this.stats.armor = Math.floor(this.statCalculator.calculate('armor'));
    this.stats.evasion = Math.floor(this.statCalculator.calculate('evasion'));

    // Recalculate attack damage from base + equipment flat damage modifiers
    const baseDamage = 25; // Base unarmed damage
    this.attackDamage = Math.floor(this.statCalculator.calculate('attackDamage', baseDamage));

    // Calculate resistances (capped at 90%)
    const allResist = this.statCalculator.calculate('allResist', 0);
    this.resistances.physical = Math.min(90, Math.floor(this.statCalculator.calculate('physicalResist', 0) + allResist));
    this.resistances.gok = Math.min(90, Math.floor(this.statCalculator.calculate('gokResist', 0) + allResist));
    this.resistances.od = Math.min(90, Math.floor(this.statCalculator.calculate('odResist', 0) + allResist));
    this.resistances.su = Math.min(90, Math.floor(this.statCalculator.calculate('suResist', 0) + allResist));
    this.resistances.karanlik = Math.min(50, Math.floor(this.statCalculator.calculate('karanlikResist', 0) + allResist)); // Chaos resist cap 50%

    // Calculate max dodge charges (base + passive extra charges)
    this.maxDodgeCharges = COMBAT.MAX_DODGE_CHARGES + Math.floor(this.statCalculator.calculate('dodgeCharges', 0));
    if (this.dodgeCharges > this.maxDodgeCharges) {
      this.dodgeCharges = this.maxDodgeCharges;
    }

    // Restore HP/Mana at same percentage (so equipping item doesn't reset to full)
    this.stats.hp = Math.floor(this.stats.maxHp * hpPercent);
    this.stats.mana = Math.floor(this.stats.maxMana * manaPercent);

    // Clamp to max
    this.stats.hp = Math.min(this.stats.hp, this.stats.maxHp);
    this.stats.mana = Math.min(this.stats.mana, this.stats.maxMana);
  }

  /**
   * Get the trigger effect manager
   */
  getTriggerManager(): TriggerEffectManager {
    return this.triggerManager;
  }

  getExperienceForNextLevel(): number {
    return Math.floor(100 * Math.pow(1.5, this.level - 1));
  }

  getExperiencePercent(): number {
    const expNeeded = this.getExperienceForNextLevel();
    return expNeeded > 0 ? this.experience / expNeeded : 0;
  }

  pickupItem(item: GameItem): boolean {
    // Check if we'll auto-equip (slot empty)
    let willAutoEquip = false;
    if ('slot' in item) {
      const equipItem = item as import('@shared/types').EquipmentItem;
      const currentEquipped = this.inventory.getEquippedItem(equipItem.slot);
      willAutoEquip = !currentEquipped;
    }

    const result = this.inventory.addItem(item, true);

    // Recalculate stats if we auto-equipped
    if (result && willAutoEquip) {
      this.recalculateStats();
    }

    return result;
  }

  /**
   * Equip an item and recalculate stats
   * @param item The item to equip
   * @param targetSlot Optional target slot (useful for rings which can go to either ring slot)
   */
  equipItem(item: import('@shared/types').EquipmentItem, targetSlot?: import('@shared/types').EquipmentSlot): import('@shared/types').EquipmentItem | null {
    const previousItem = this.inventory.equipItem(item, targetSlot);
    this._equipHashDirty = true;
    this.recalculateStats();
    return previousItem;
  }

  /**
   * Unequip an item and recalculate stats
   */
  unequipItem(slot: import('@shared/types').EquipmentSlot): import('@shared/types').EquipmentItem | null {
    const item = this.inventory.unequipItem(slot);
    this._equipHashDirty = true;
    this.recalculateStats();
    return item;
  }

  getGold(): number {
    return this.inventory.getGold();
  }

  // Override takeDamage to add visual feedback and Mind Over Matter support
  // sourcePosition: optional position of the damage source for directional knockback
  takeDamage(amount: number, type?: import('@shared/types').ElementType, sourcePosition?: Vector2): number {
    if (!this.isAlive || this.invincible) return 0;

    // Evasion check: evasion / (evasion + 200) chance to dodge
    if (this.stats.evasion > 0) {
      const dodgeChance = this.stats.evasion / (this.stats.evasion + 200);
      if (Math.random() < dodgeChance) {
        getDamageNumbers().spawn(this.position.x, this.position.y - 30, 0, 'dodge');
        getTriggerEffectManager().processTrigger('on_dodge', this.statCalculator, { damageType: type || 'physical' });
        return 0;
      }
    }

    // Block check
    if (this.stats.blockChance && this.stats.blockChance > 0) {
      if (Math.random() < this.stats.blockChance / 100) {
        getDamageNumbers().spawn(this.position.x, this.position.y - 30, 0, 'block');
        getAudioManager().play('block');
        getTriggerEffectManager().processTrigger('on_block', this.statCalculator, { damageType: type || 'physical' });
        return 0;
      }
    }

    let finalDamage = Math.floor(amount);

    // Apply armor for physical damage
    if ((!type || type === 'physical') && this.stats.armor > 0) {
      const reduction = this.stats.armor / (this.stats.armor + COMBAT.ARMOR_CONSTANT * this.level);
      finalDamage = Math.floor(amount * (1 - Math.min(reduction, COMBAT.MAX_ARMOR_REDUCTION)));
    }
    // Apply elemental resistance for elemental damage
    else if (type && type !== 'physical') {
      const elementKey = type as keyof typeof this.resistances;
      if (elementKey in this.resistances) {
        const resistance = this.resistances[elementKey];
        finalDamage = Math.floor(amount * (1 - resistance / 100));
      }
    }

    // Mind Over Matter: 30% of damage goes to mana first
    if (this.hasMindOverMatter() && this.stats.mana > 0) {
      const manaAbsorb = Math.floor(finalDamage * 0.3);
      const actualManaAbsorb = Math.min(manaAbsorb, this.stats.mana);

      this.stats.mana -= actualManaAbsorb;
      finalDamage -= actualManaAbsorb;
    }

    // Reset ES recharge delay on any damage
    this.esRechargeDelay = 2.0;

    // Apply energy shield first
    let hpDamage = finalDamage;
    if (this.stats.energyShield > 0) {
      if (this.stats.energyShield >= finalDamage) {
        this.stats.energyShield -= finalDamage;
        this.hitFlashTimer = this.hitFlashDuration;
        return 0; // No HP damage dealt - shield absorbed everything
      } else {
        hpDamage = finalDamage - this.stats.energyShield;
        this.stats.energyShield = 0;
      }
    }

    // Apply to HP
    const actualHpDamage = Math.min(hpDamage, this.stats.hp);
    this.stats.hp = Math.max(0, this.stats.hp - actualHpDamage);

    if (this.stats.hp <= 0) {
      this.die();
    }

    if (hpDamage > 0) {
      this.hitFlashTimer = this.hitFlashDuration; // 150ms white-to-red flash
      getAudioManager().play(Math.random() < 0.5 ? 'hit_soft' : 'hit_soft_1');

      // Knockback effect: 15-25px away from damage source over 0.1s
      const knockbackStrength = 15 + Math.random() * 10; // 15-25px
      let kbAngle: number;

      if (sourcePosition) {
        // Directional knockback: push away from damage source
        const dx = this.position.x - sourcePosition.x;
        const dy = this.position.y - sourcePosition.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0) {
          kbAngle = Math.atan2(dy, dx);
        } else {
          kbAngle = Math.random() * Math.PI * 2;
        }
      } else {
        // Fallback: random direction if no source provided
        kbAngle = Math.random() * Math.PI * 2;
      }

      // Apply knockback velocity (decays over ~0.1s via kbDecay=10 in update)
      this.knockbackVelocity = {
        x: Math.cos(kbAngle) * knockbackStrength * 10,
        y: Math.sin(kbAngle) * knockbackStrength * 10,
      };
    }

    // Spawn floating damage number at player position
    if (actualHpDamage > 0) {
      const dmgType = (type && type !== 'physical') ? type : 'physical';
      getDamageNumbers().spawn(
        this.position.x,
        this.position.y - this.height,
        actualHpDamage,
        dmgType as import('@client/systems/DamageNumbers').DamageNumberType
      );
    }

    // Process on_take_damage triggers
    if (actualHpDamage > 0) {
      const triggerMgr = getTriggerEffectManager();
      const eventData: import('@shared/modifiers').TriggerEventData = { damageDealt: actualHpDamage, damageType: type || 'physical' };
      triggerMgr.processTrigger('on_take_damage', this.statCalculator, eventData);

      // on_low_hp: trigger when HP drops below 35%
      if (this.stats.hp > 0 && this.stats.hp / this.stats.maxHp < 0.35) {
        triggerMgr.processTrigger('on_low_hp', this.statCalculator, eventData);
      }
    }

    return actualHpDamage; // Return actual HP damage for trigger calculations (leech, etc.)
  }

  // Serialize skills for saving
  serializeSkills(): { customSkills: object[]; customSkillsV2: object[]; hotbar: (string | null)[]; activeAuras: string[]; reservedMana: number; reservedManaFlat: number } {
    return {
      customSkills: this.customSkills.map(skill => SkillCreator.serialize(skill)),
      customSkillsV2: this.customSkillsV2.map(skill => SkillCreatorV2.serialize(skill)),
      hotbar: this.skillHotbar.slots.map(skill => skill?.id || null),
      activeAuras: Array.from(this.activeAuras),
      reservedMana: this.reservedMana,
      reservedManaFlat: this.reservedManaFlat,
    };
  }

  /**
   * Called when a skill is modified (stone socketed/unsocketed, slider changed)
   * Triggers stat recalculation
   */
  onSkillModified(): void {
    this.recalculateStats();
  }

  /**
   * Socket an effect stone into a skill
   */
  socketStoneToSkill(skillId: string, slotIndex: number, stone: import('@shared/skills/SkillCreator').EffectStone): import('@shared/skills/SkillCreator').EffectStone | null {
    const skill = this.customSkills.find(s => s.id === skillId);
    if (!skill) return null;

    const previous = SkillCreator.socketStone(skill, slotIndex, stone);
    this.onSkillModified();
    return previous;
  }

  /**
   * Unsocket an effect stone from a skill
   */
  unsocketStoneFromSkill(skillId: string, slotIndex: number): import('@shared/skills/SkillCreator').EffectStone | null {
    const skill = this.customSkills.find(s => s.id === skillId);
    if (!skill) return null;

    const stone = SkillCreator.unsocketStone(skill, slotIndex);
    this.onSkillModified();
    return stone;
  }

  // Deserialize skills from save data
  deserializeSkills(data: { customSkills?: object[]; customSkillsV2?: object[]; hotbar?: (string | null)[]; activeAuras?: string[]; reservedMana?: number; reservedManaFlat?: number }): void {
    if (!data) return;

    const hasV1 = data.customSkills && data.customSkills.length > 0;
    const hasV2 = data.customSkillsV2 && data.customSkillsV2.length > 0;

    // If no saved skills at all, use starter skills
    if (!hasV1 && !hasV2) {
      this.initializeSkills();
      return;
    }

    // Deserialize V1 skills
    this.customSkills = [];
    if (hasV1) {
      for (const savedSkill of data.customSkills!) {
        const skill = SkillCreator.deserialize(savedSkill as Record<string, unknown>);
        if (skill) {
          this.customSkills.push(skill);
        }
      }
    }

    // Deserialize V2 skills
    this.customSkillsV2 = [];
    if (hasV2) {
      for (const savedSkill of data.customSkillsV2!) {
        const skill = SkillCreatorV2.deserialize(savedSkill as Record<string, unknown>);
        if (skill) {
          this.customSkillsV2.push(skill);
        }
      }
    }

    // Fallback: if deserialization failed for all skills, use starters
    if (this.customSkills.length === 0 && this.customSkillsV2.length === 0) {
      this.initializeSkills();
    }

    // Restore hotbar assignments
    if (data.hotbar) {
      for (let i = 0; i < data.hotbar.length; i++) {
        const skillId = data.hotbar[i];
        if (skillId) {
          // Search in both V1 and V2 skill lists
          const v1Skill = this.customSkills.find(s => s.id === skillId);
          const v2Skill = this.customSkillsV2.find(s => s.id === skillId);
          const skill = v1Skill || v2Skill;
          if (skill) {
            this.skillHotbar.assign(skill, i);
          }
        }
      }
    }

    // Restore aura state (validate each aura ID corresponds to an existing skill)
    if (data.activeAuras && Array.isArray(data.activeAuras)) {
      const validAuras: string[] = [];
      for (const auraId of data.activeAuras as string[]) {
        const v1Match = this.customSkills.find(s => s.id === auraId);
        const v2Match = this.customSkillsV2.find(s => s.id === auraId);
        if (v1Match || v2Match) {
          validAuras.push(auraId);
        } else {
          debugLog('Player', `Skipping invalid aura ID on load: ${auraId}`);
        }
      }
      this.activeAuras = new Set(validAuras);
      this._auraBonusDirty = true;
    }
    if (typeof data.reservedMana === 'number') {
      this.reservedMana = data.reservedMana;
    }
    if (typeof data.reservedManaFlat === 'number') {
      this.reservedManaFlat = data.reservedManaFlat;
    }
  }
}
